import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api";
import { IDCardFront, IDCardBack } from "../components/IDCardFaces.jsx";
import NewHirePacketTab from "../components/NewHirePacketTab.jsx";

const SKILL_OPTIONS = [
  "Personal care", "Bathing", "Grooming", "Dressing", "Toileting", "Incontinence care",
  "Meal preparation", "Light housekeeping", "Laundry", "Medication reminders",
  "Ambulation assistance", "Transfer assistance", "Hoyer lift", "Wheelchair assistance",
  "Dementia care", "Alzheimer's care", "Parkinson's care", "Hospice support",
  "Fall prevention", "Range-of-motion exercises", "Transportation", "Catheter care",
  "Feeding assistance", "Vital signs", "Documentation", "Electronic visit verification",
];
const DOCUMENT_TYPES = [
  "Employment Contract", "W-4", "I-9", "Driver's License", "Social Security Card",
  "CPR Certificate", "First Aid Certificate", "CNA/HHA License", "Professional License",
  "Vehicle Registration", "Auto Insurance", "Physical Examination", "TB Screening",
  "Background Check Report", "BAAS Registry Check (NH)", "Abuse Registry Result",
  "Drug Test Result", "Vaccination Record", "Motor Vehicle Record",
  "Confidentiality Agreement", "Employee NDA", "Employee Handbook Acknowledgment", "Orientation Certificate",
  "Orientation Checklist", "Employment Application", "Resume", "Offer Letter", "Signed Job Description",
  "Annual Non-Conviction Attestation", "HIPAA Training Certificate", "HIPAA Acknowledgment",
  "Infection Control Training", "Standard Precautions Training", "Dementia Training Certificate",
  "PCA Competency Evaluation", "Competency Evaluation", "LNA License",
];
const COMPLIANCE_COLORS = {
  "Compliant": "bg-green-50 text-green-700",
  "Expiring Soon": "bg-yellow-50 text-yellow-700",
  "Non-Compliant": "bg-red-50 text-red-700",
  "No Documents": "bg-gray-100 text-gray-500",
};

function docStatus(doc) {
  if (!doc.expiration_date) return { label: "No Expiration", color: "text-gray-400" };
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  if (doc.expiration_date < today) return { label: "Expired", color: "text-red-600 font-medium" };
  if (doc.expiration_date <= in30) return { label: "Expiring Soon", color: "text-yellow-600 font-medium" };
  return { label: "Valid", color: "text-green-600" };
}

export default function EmployeeProfile() {
  const { employeeId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Summary");
  const [showTerminate, setShowTerminate] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.employees.get(employeeId).then(setData).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [employeeId]);

  const reinstate = async () => {
    setBusy(true);
    try {
      await api.employees.reinstate(employeeId);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="text-red-600">{error}</div>;
  if (!data) return <div className="text-gray-500">Loading…</div>;

  const isFieldDuty = data.staff_category === "Field Duty Staff";
  const isTerminated = data.employment_status === "Terminated";
  // Exit Interview stays available once there's ever been a termination
  // date on record, even after Reinstate — it's a historical event, not
  // tied to current status.
  const hasTerminationHistory = Boolean(data.termination_date);
  const baseTabs = isFieldDuty
    ? ["Summary", "Onboarding", "New Hire Packet", "Skills", "Documents", "Compliance", "Client Assignments"]
    : ["Summary", "Onboarding", "New Hire Packet", "Documents", "Compliance"];
  const tabsBeforeHistory = hasTerminationHistory ? [...baseTabs, "Exit Interview"] : baseTabs;
  const tabs = [...tabsBeforeHistory, "History"];

  return (
    <div>
      <Link to="/admin/employees" className="text-sm hover:underline" style={{ color: "#154E93" }}>← Back to Employees</Link>
      <div className="flex items-center justify-between mt-2 mb-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>{data.first_name} {data.last_name}</h1>
          <p className="text-sm text-gray-500">
            {data.employee_id} · {data.staff_category}{data.caregiver_type ? ` · ${data.caregiver_type}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data.compliance_status && (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${COMPLIANCE_COLORS[data.compliance_status]}`}>
              {data.compliance_status}
            </span>
          )}
          <Link to={`/admin/employees/${employeeId}/edit`} className="px-3 py-1.5 text-sm rounded border" style={{ borderColor: "#154E93", color: "#154E93" }}>
            Edit
          </Link>
          {isTerminated ? (
            <button
              onClick={reinstate}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded border border-green-600 text-green-700 disabled:opacity-60"
            >
              {busy ? "Reinstating…" : "Reinstate"}
            </button>
          ) : (
            <button
              onClick={() => setShowTerminate(true)}
              className="px-3 py-1.5 text-sm rounded border border-red-300 text-red-600"
            >
              Terminate
            </button>
          )}
        </div>
      </div>

      {isTerminated && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm">
          <div className="font-medium text-red-700 mb-1">Terminated {data.termination_date ? `on ${data.termination_date}` : ""}</div>
          {data.termination_reason && <div className="text-red-600">Reason: {data.termination_reason}</div>}
          {data.rehire_eligible !== null && data.rehire_eligible !== undefined && (
            <div className="text-red-600">Rehire eligible: {data.rehire_eligible ? "Yes" : "No"}</div>
          )}
          {data.termination_notes && <div className="text-red-600 mt-1">{data.termination_notes}</div>}
        </div>
      )}

      {showTerminate && (
        <TerminateModal
          employeeId={employeeId}
          onClose={() => setShowTerminate(false)}
          onDone={() => { setShowTerminate(false); load(); }}
        />
      )}

      {tabs.length > 1 && (
        <div className="flex gap-1 mb-4 border-b">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t ? "border-current" : "border-transparent text-gray-500"}`}
              style={tab === t ? { color: "#154E93" } : {}}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {tab === "Summary" && <SummaryTab employeeId={employeeId} data={data} isFieldDuty={isFieldDuty} onChange={load} />}
      {tab === "Onboarding" && <OnboardingTab employeeId={employeeId} tasks={data.onboarding_tasks || []} documents={data.documents || []} onChange={load} onOpenDocuments={() => setTab("Documents")} />}
      {tab === "New Hire Packet" && <NewHirePacketTab employeeId={employeeId} />}
      {tab === "Skills" && <SkillsTab employeeId={employeeId} skills={data.skills || []} onChange={load} />}
      {tab === "Documents" && <DocumentsTab employeeId={employeeId} documents={data.documents || []} onChange={load} />}
      {tab === "Compliance" && <ComplianceCenter employeeId={employeeId} employee={data} onDocumentsChange={load} onOpenDocuments={() => setTab("Documents")} />}
      {tab === "Client Assignments" && <AssignmentsTab employeeId={employeeId} assignments={data.assignments || []} onChange={load} />}
      {tab === "Exit Interview" && <ExitInterviewTab employeeId={employeeId} interviews={data.exit_interviews || []} onChange={load} />}
      {tab === "History" && <EmployeeTimeline employeeId={employeeId} initialEvents={data.timeline || []} fullView />}
    </div>
  );
}

const TERMINATION_REASONS = [
  "Voluntary Resignation", "Involuntary — Performance", "Involuntary — Policy Violation",
  "Involuntary — Attendance", "Layoff / Position Eliminated", "End of Contract",
  "Deceased", "Other",
];

function TerminateModal({ employeeId, onClose, onDone }) {
  const [terminationDate, setTerminationDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [rehireEligible, setRehireEligible] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await api.employees.terminate(employeeId, {
        termination_date: terminationDate,
        termination_reason: reason || null,
        rehire_eligible: rehireEligible === "" ? null : rehireEligible === "yes",
        termination_notes: notes || null,
      });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-5 max-w-md w-full">
        <h2 className="font-semibold text-lg mb-1" style={{ color: "#154E93" }}>Terminate employment</h2>
        <p className="text-xs text-gray-500 mb-4">
          This closes any active client assignments and frees up any not-yet-completed shifts so they can be reassigned.
        </p>
        {error && <div className="mb-3 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Termination Date</label>
            <input type="date" className="w-full border rounded px-2 py-1.5 text-sm" value={terminationDate} onChange={(e) => setTerminationDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">Select…</option>
              {TERMINATION_REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Rehire Eligible</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={rehireEligible} onChange={(e) => setRehireEligible(e.target.value)}>
              <option value="">Undecided</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Internal Notes (optional)</label>
            <textarea rows={2} className="w-full border rounded px-2 py-1.5 text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-3 py-1.5 text-sm rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
          >
            {saving ? "Terminating…" : "Confirm Termination"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b last:border-0 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium">{value || "—"}</span>
    </div>
  );
}

function SecureSsnRow({ employeeId, last4 }) {
  const [revealed, setRevealed] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reveal = async () => {
    setLoading(true); setError("");
    try {
      const result = await api.employees.revealSsn(employeeId);
      setRevealed(result.ssn);
      window.setTimeout(() => setRevealed(""), 30000);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="py-2 border-b">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-gray-500">Social Security Number</span>
        <div className="flex items-center gap-2">
          <span className="font-medium tracking-wide">{revealed || (last4 ? `XXX-XX-${last4}` : "—")}</span>
          {last4 && <button type="button" onClick={reveal} disabled={loading} className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50">{loading ? "Opening…" : revealed ? "Shown 30s" : "Reveal"}</button>}
        </div>
      </div>
      {error && <div className="text-[11px] text-red-600 text-right mt-1">{error}</div>}
    </div>
  );
}

function StatusPill({ value }) {
  const normalized = String(value || "Unknown").toLowerCase();
  const colors = normalized.includes("active") || normalized.includes("compliant") || normalized.includes("complete") || normalized.includes("approved")
    ? "bg-green-50 text-green-700 border-green-200"
    : normalized.includes("terminated") || normalized.includes("non-compliant") || normalized.includes("expired") || normalized.includes("missing")
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-semibold ${colors}`}>{value || "Unknown"}</span>;
}

function MetricCard({ label, value, helper, tone = "blue", icon = "•", progress = null }) {
  const tones = {
    blue: "from-blue-50 to-white border-blue-100 text-blue-700",
    green: "from-emerald-50 to-white border-emerald-100 text-emerald-700",
    amber: "from-amber-50 to-white border-amber-100 text-amber-700",
    purple: "from-violet-50 to-white border-violet-100 text-violet-700",
  };
  return (
    <div className={`rounded-2xl border bg-gradient-to-br p-4 shadow-sm hover:shadow-md transition-shadow ${tones[tone] || tones.blue}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-1 text-gray-900">{value}</div>
        </div>
        <span className="w-9 h-9 rounded-xl bg-white/80 border border-white flex items-center justify-center text-lg shadow-sm">{icon}</span>
      </div>
      {progress != null && <div className="mt-3 h-1.5 rounded-full bg-white/80 overflow-hidden"><div className="h-full rounded-full bg-current opacity-60" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} /></div>}
      {helper && <div className="text-[11px] mt-2 text-gray-500">{helper}</div>}
    </div>
  );
}

function formatFriendlyDate(value) {
  if (!value) return "—";
  const date = new Date(String(value).includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function getDocumentCompliance(documents = []) {
  const today = new Date();
  const soon = new Date(Date.now() + 30 * 86400000);
  const groups = [
    { label: "Background Check", matches: ["Background Check Report", "BAAS Registry Check (NH)"] },
    { label: "CPR / First Aid", matches: ["CPR Certificate", "First Aid Certificate"] },
    { label: "Identity & Work Eligibility", matches: ["I-9", "Social Security Card"] },
    { label: "Health Screening", matches: ["Physical Examination", "TB Screening"] },
    { label: "Driver / Auto", matches: ["Driver's License", "Auto Insurance"] },
    { label: "Orientation", matches: ["Orientation Certificate", "Employee Handbook Acknowledgment"] },
  ];
  return groups.map((group) => {
    const matched = documents.filter((d) => group.matches.includes(d.document_type));
    if (!matched.length) return { ...group, state: "Missing", detail: "Not uploaded" };
    const expired = matched.find((d) => d.expiration_date && new Date(`${d.expiration_date}T23:59:59`) < today);
    if (expired) return { ...group, state: "Expired", detail: `Expired ${formatFriendlyDate(expired.expiration_date)}` };
    const expiring = matched.find((d) => d.expiration_date && new Date(`${d.expiration_date}T23:59:59`) <= soon);
    if (expiring) return { ...group, state: "Expiring Soon", detail: `Due ${formatFriendlyDate(expiring.expiration_date)}` };
    return { ...group, state: "Complete", detail: matched.length > 1 ? `${matched.length} records` : "On file" };
  });
}

function CompliancePanel({ documents = [] }) {
  const rows = getDocumentCompliance(documents);
  const complete = rows.filter((row) => row.state === "Complete").length;
  const percent = Math.round((complete / rows.length) * 100);
  const icon = (state) => state === "Complete" ? "✓" : state === "Expiring Soon" ? "!" : "×";
  const color = (state) => state === "Complete"
    ? "bg-green-100 text-green-700"
    : state === "Expiring Soon"
      ? "bg-amber-100 text-amber-700"
      : "bg-red-100 text-red-700";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold" style={{ color: "#154E93" }}>Compliance Readiness</h3>
          <p className="text-xs text-gray-500">Required employment and field-readiness records</p>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-gray-900">{percent}%</div>
          <div className="text-[10px] text-gray-400">ready</div>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: percent === 100 ? "#16a34a" : "#154E93" }} />
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold shrink-0 ${color(row.state)}`}>{icon(row.state)}</span>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-gray-800 truncate">{row.label}</div>
                <div className="text-[10px] text-gray-400 truncate">{row.detail}</div>
              </div>
            </div>
            <span className="text-[10px] text-gray-500 shrink-0">{row.state}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OnboardingPanel({ tasks = [], documents = [], onOpenDocuments = () => {} }) {
  const completed = tasks.filter((task) => task.status === "Completed").length;
  const percent = tasks.length ? Math.round((completed / tasks.length) * 100) : 0;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex justify-between items-start gap-4 mb-3">
        <div>
          <h3 className="font-semibold" style={{ color: "#154E93" }}>Onboarding Progress</h3>
          <p className="text-xs text-gray-500">{completed} of {tasks.length} requirements completed</p>
        </div>
        <div className="w-14 h-14 rounded-full flex items-center justify-center border-[5px] border-blue-100 text-sm font-bold" style={{ color: "#154E93" }}>{percent}%</div>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mb-4">
        <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, backgroundColor: "#154E93" }} />
      </div>
      {tasks.length === 0 ? (
        <p className="text-sm text-gray-400">No onboarding checklist has been started.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {tasks.slice(0, 8).map((task) => {
            const done = task.status === "Completed";
            return (
              <div key={task.id} className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${done ? "bg-green-50 border-green-100" : "bg-gray-50 border-gray-100"}`}>
                <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[11px] font-bold ${done ? "bg-green-600 text-white" : "bg-white border text-gray-400"}`}>{done ? "✓" : ""}</span>
                <span className={`text-xs ${done ? "text-green-800" : "text-gray-600"}`}>{task.task_name || task.title || "Onboarding task"}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function buildRenewals(data) {
  const items = [];
  const add = (label, date, source) => {
    if (!date) return;
    const due = new Date(`${date}T23:59:59`);
    const days = Math.ceil((due.getTime() - Date.now()) / 86400000);
    items.push({ label, date, days, source });
  };
  (data.documents || []).forEach((doc) => add(doc.document_type, doc.expiration_date, "Document"));
  add("Driver's License", data.driver_license_expiration, "Transportation");
  add("Auto Insurance", data.vehicle_insurance_expiration, "Transportation");
  add("Employee ID Card", data.id_card_expiration_date, "Identity");
  return items.sort((a, b) => new Date(a.date) - new Date(b.date));
}

function ReadinessPanel({ data }) {
  const tasks = data.onboarding_tasks || [];
  const onboardingComplete = tasks.length > 0 && tasks.every((task) => task.status === "Completed");
  const complianceRows = getDocumentCompliance(data.documents || []);
  const complianceReady = complianceRows.every((row) => row.state === "Complete");
  const checks = [
    { label: "Employee record active", done: data.employment_status === "Active" },
    { label: "Onboarding completed", done: onboardingComplete },
    { label: "Compliance documents ready", done: complianceReady },
    { label: "Employee photo approved", done: Boolean(data.photo_approved) },
    { label: "ID card dates entered", done: Boolean(data.id_card_issue_date && data.id_card_expiration_date) },
  ];
  const done = checks.filter((item) => item.done).length;
  const score = Math.round((done / checks.length) * 100);
  const ready = score === 100;

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${ready ? "bg-emerald-50 border-emerald-200" : "bg-gradient-to-br from-blue-50 to-white border-blue-100"}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold" style={{ color: ready ? "#047857" : "#154E93" }}>Scheduling Readiness</h3>
          <p className="text-xs text-gray-500">Employment activation checklist</p>
        </div>
        <div className={`w-14 h-14 rounded-full border-[5px] flex items-center justify-center text-sm font-bold ${ready ? "border-emerald-200 text-emerald-700 bg-white" : "border-blue-100 text-blue-800 bg-white"}`}>{score}%</div>
      </div>
      <div className="space-y-2">
        {checks.map((item) => (
          <div key={item.label} className="flex items-center gap-2 text-xs">
            <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center font-bold ${item.done ? "bg-emerald-600 text-white" : "bg-white border border-gray-300 text-gray-300"}`}>{item.done ? "✓" : ""}</span>
            <span className={item.done ? "text-gray-700" : "text-gray-500"}>{item.label}</span>
          </div>
        ))}
      </div>
      <div className={`mt-3 rounded-lg px-3 py-2 text-xs font-semibold text-center ${ready ? "bg-emerald-600 text-white" : "bg-blue-900 text-white"}`}>
        {ready ? "Ready for scheduling and client assignment" : `${checks.length - done} requirement${checks.length - done === 1 ? "" : "s"} remaining`}
      </div>
    </div>
  );
}

function RenewalsPanel({ data }) {
  const renewals = buildRenewals(data);
  const visible = renewals.slice(0, 5);
  const tone = (days) => days < 0 ? "bg-red-100 text-red-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700";
  const text = (days) => days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Due today" : `${days}d left`;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div><h3 className="font-semibold" style={{ color: "#154E93" }}>Upcoming Renewals</h3><p className="text-xs text-gray-500">Documents and credentials requiring attention</p></div>
        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{renewals.length}</span>
      </div>
      {visible.length === 0 ? <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-400 text-center">No expiration dates recorded.</div> : (
        <div className="space-y-2">
          {visible.map((item, index) => (
            <div key={`${item.label}-${item.date}-${index}`} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
              <div className="min-w-0"><div className="text-xs font-semibold text-gray-800 truncate">{item.label}</div><div className="text-[10px] text-gray-400">{formatFriendlyDate(item.date)} · {item.source}</div></div>
              <span className={`text-[10px] font-semibold rounded-full px-2 py-1 shrink-0 ${tone(item.days)}`}>{text(item.days)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActionPriorityPanel({ data }) {
  const tasks = data.onboarding_tasks || [];
  const renewals = buildRenewals(data);
  const priorities = [];
  if (!data.photo) priorities.push({ label: "Upload employee photo", detail: "Required for the ID card", tone: "red" });
  else if (!data.photo_approved) priorities.push({ label: "Approve employee photo", detail: "Unlock ID card issuance", tone: "amber" });
  if (!data.id_card_issue_date || !data.id_card_expiration_date) priorities.push({ label: "Complete ID card dates", detail: "Issue and expiration dates are required", tone: "amber" });
  const pending = tasks.filter((task) => task.status !== "Completed");
  if (pending.length) priorities.push({ label: `${pending.length} onboarding item${pending.length === 1 ? "" : "s"} pending`, detail: pending[0]?.task_name || pending[0]?.title || "Open onboarding checklist", tone: "blue" });
  const urgent = renewals.find((item) => item.days <= 30);
  if (urgent) priorities.push({ label: urgent.days < 0 ? `${urgent.label} overdue` : `${urgent.label} expires soon`, detail: formatFriendlyDate(urgent.date), tone: urgent.days < 0 ? "red" : "amber" });
  const visible = priorities.slice(0, 4);
  const tones = { red: "bg-red-50 border-red-100 text-red-700", amber: "bg-amber-50 border-amber-100 text-amber-700", blue: "bg-blue-50 border-blue-100 text-blue-700" };
  return (
    <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3"><div><h3 className="font-semibold">HR Assistant</h3><p className="text-xs text-blue-200">Priority actions for this employee</p></div><span className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">✦</span></div>
      {visible.length === 0 ? <div className="rounded-xl bg-emerald-400/15 border border-emerald-300/20 p-3 text-sm text-emerald-100">✓ No urgent actions. This employee record is in good standing.</div> : <div className="space-y-2">{visible.map((item, index) => <div key={`${item.label}-${index}`} className={`rounded-xl border p-3 ${tones[item.tone]}`}><div className="text-xs font-semibold">{item.label}</div><div className="text-[10px] opacity-75 mt-0.5">{item.detail}</div></div>)}</div>}
    </div>
  );
}

function WorkforceInsights({ insights = {} }) {
  const attendance = insights.attendance || {};
  const performance = insights.performance;
  const training = insights.training || {};
  const schedule = insights.schedule || {};
  const perf = performance?.overall_score != null ? Number(performance.overall_score).toFixed(1) : "—";
  const attendanceRate = attendance.days_recorded ? `${Math.round(attendance.attendance_rate || 0)}%` : "—";
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="mb-3"><h3 className="font-semibold" style={{ color: "#154E93" }}>Workforce Snapshot</h3><p className="text-xs text-gray-500">Live activity and performance indicators</p></div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-blue-50 p-3"><div className="text-[10px] uppercase font-semibold text-blue-500">Hours This Week</div><div className="text-xl font-bold text-gray-900">{Number(attendance.hours_this_week || 0).toFixed(1)}</div></div>
        <div className="rounded-lg bg-emerald-50 p-3"><div className="text-[10px] uppercase font-semibold text-emerald-600">Attendance</div><div className="text-xl font-bold text-gray-900">{attendanceRate}</div></div>
        <div className="rounded-lg bg-violet-50 p-3"><div className="text-[10px] uppercase font-semibold text-violet-600">Training</div><div className="text-xl font-bold text-gray-900">{training.completed || 0}</div><div className="text-[10px] text-gray-400">courses recorded</div></div>
        <div className="rounded-lg bg-amber-50 p-3"><div className="text-[10px] uppercase font-semibold text-amber-600">Performance</div><div className="text-xl font-bold text-gray-900">{perf}</div><div className="text-[10px] text-gray-400">latest overall score</div></div>
      </div>
      <div className="mt-3 pt-3 border-t text-xs flex justify-between"><span className="text-gray-500">Shifts this week</span><span className="font-semibold">{schedule.shifts_this_week || 0}</span></div>
      <div className="py-2 text-xs flex justify-between border-t"><span className="text-gray-500">Next shift</span><span className="font-semibold text-right">{schedule.next_shift ? formatTimelineDate(schedule.next_shift) : "Not scheduled"}</span></div>
    </div>
  );
}

function SummaryTab({ employeeId, data, isFieldDuty, onChange }) {
  const displayName = `${data.preferred_name || data.first_name || ""} ${data.last_name || ""}`.trim();
  const onboarding = data.onboarding_tasks || [];
  const activeAssignments = (data.assignments || []).filter((a) => a.status === "Active").length;
  const validDocuments = (data.documents || []).filter((doc) => docStatus(doc).label === "Valid" || docStatus(doc).label === "No Expiration").length;
  const totalDocuments = (data.documents || []).length;
  const yearsOfService = data.employment_date
    ? Math.max(0, (Date.now() - new Date(`${data.employment_date}T00:00:00`).getTime()) / 31557600000).toFixed(1)
    : "—";

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-950 via-blue-800 to-blue-700 rounded-2xl p-5 text-white shadow-sm overflow-hidden relative">
        <div className="absolute -right-20 -top-24 w-64 h-64 rounded-full bg-white/10" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-20 h-20 rounded-2xl bg-white/15 border border-white/20 overflow-hidden flex items-center justify-center text-2xl font-bold shrink-0 shadow-lg">
              {data.photo ? <img src={`${api.employees.photoUrl(employeeId)}?t=${data.updated_at || ""}`} alt={displayName} className="w-full h-full object-cover" /> : <span>{(data.first_name || "E")[0]}{(data.last_name || "")[0]}</span>}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h2 className="text-2xl font-bold truncate">{displayName}</h2>
                <span className="px-2.5 py-1 rounded-full bg-green-400/20 border border-green-300/30 text-green-100 text-xs font-semibold">{data.employment_status || "Active"}</span>
              </div>
              <p className="text-blue-100 text-sm">{data.position || data.caregiver_type || "Position not entered"} · {data.department || data.staff_category || "Department not entered"}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-blue-100">
                <span>ID: {data.employee_id}</span>
                <span>Hired: {formatFriendlyDate(data.employment_date)}</span>
                <span>Branch: {data.branch_office || "Not assigned"}</span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap justify-end gap-2">
              {data.phone && <a href={`tel:${data.phone}`} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 text-xs font-semibold hover:bg-white/20">Call</a>}
              {(data.work_email || data.personal_email || data.email) && <a href={`mailto:${data.work_email || data.personal_email || data.email}`} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/15 text-xs font-semibold hover:bg-white/20">Email</a>}
              <Link to={`/admin/employees/${employeeId}/edit`} className="px-3 py-1.5 rounded-lg bg-white text-blue-900 text-xs font-semibold hover:bg-blue-50">Edit Profile</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-[320px]">
            <div className="rounded-xl bg-white/10 border border-white/10 px-3 py-2"><div className="text-[10px] text-blue-100 uppercase">Service</div><div className="font-bold">{yearsOfService} yrs</div></div>
            <div className="rounded-xl bg-white/10 border border-white/10 px-3 py-2"><div className="text-[10px] text-blue-100 uppercase">Clients</div><div className="font-bold">{activeAssignments}</div></div>
            <div className="rounded-xl bg-white/10 border border-white/10 px-3 py-2"><div className="text-[10px] text-blue-100 uppercase">Documents</div><div className="font-bold">{validDocuments}/{totalDocuments}</div></div>
            <div className="rounded-xl bg-white/10 border border-white/10 px-3 py-2"><div className="text-[10px] text-blue-100 uppercase">Photo</div><div className="font-bold">{data.photo_approved ? "Approved" : "Pending"}</div></div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Hours This Week" value={Number(data.insights?.attendance?.hours_this_week || 0).toFixed(1)} helper="Approved attendance hours" tone="blue" icon="◷" progress={Math.min(100, Number(data.insights?.attendance?.hours_this_week || 0) / Math.max(1, Number(data.max_weekly_hours || 40)) * 100)} />
        <MetricCard label="Active Clients" value={activeAssignments} helper="Current assignments" tone="purple" icon="⌂" />
        <MetricCard label="Compliance" value={data.compliance_status || "No Documents"} helper={`${validDocuments} of ${totalDocuments} documents current`} tone="green" icon="✓" progress={totalDocuments ? validDocuments / totalDocuments * 100 : 0} />
        <MetricCard label="Performance" value={data.insights?.performance?.overall_score != null ? Number(data.insights.performance.overall_score).toFixed(1) : "—"} helper="Latest review score" tone="amber" icon="★" progress={data.insights?.performance?.overall_score != null ? Number(data.insights.performance.overall_score) * 20 : 0} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        <div className="xl:col-span-3 space-y-4">
          <PhotoWidget employeeId={employeeId} data={data} onChange={onChange} />

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="font-semibold mb-3" style={{ color: "#154E93" }}>Employee Quick Card</h3>
            <InfoRow label="Employee ID" value={data.employee_id} />
            <InfoRow label="Staff Category" value={data.staff_category} />
            <InfoRow label="Employment Type" value={data.employment_type} />
            <InfoRow label="Hire Date" value={formatFriendlyDate(data.employment_date)} />
            <InfoRow label="Time Tracking ID" value={data.time_tracking_id} />
            <div className="flex justify-between items-center py-2 border-b"><span className="text-sm text-gray-500">Portal</span><StatusPill value={data.portal_status || "Disabled"} /></div>
            <div className="flex justify-between items-center py-2 border-b"><span className="text-sm text-gray-500">Employment</span><StatusPill value={data.employment_status} /></div>
            <div className="flex justify-between items-center py-2"><span className="text-sm text-gray-500">Compliance</span><StatusPill value={data.compliance_status} /></div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between gap-3 mb-3"><h3 className="font-semibold" style={{ color: "#154E93" }}>Portal Access</h3><StatusPill value={data.portal_status || "Disabled"} /></div>
            <InfoRow label="Username" value={data.username} />
            <InfoRow label="Mobile App" value={data.mobile_app_enabled ? "Enabled" : "Disabled"} />
            <InfoRow label="Notifications" value={data.preferred_notification} />
            <InfoRow label="MFA" value={data.mfa_enabled ? "Enabled" : "Not enabled"} />
            <InfoRow label="Last Login" value={data.last_login ? new Date(data.last_login).toLocaleString() : "Never"} />
            {data.password_reset_required ? <div className="mt-3 rounded-lg bg-amber-50 text-amber-700 px-3 py-2 text-xs font-medium">Password reset required at next login.</div> : null}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <h3 className="font-semibold mb-3" style={{ color: "#154E93" }}>Quick Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              <a href={data.phone ? `tel:${data.phone}` : undefined} className={`px-3 py-2 rounded-lg border text-sm text-center ${data.phone ? "hover:bg-blue-50 hover:border-blue-200" : "text-gray-300 cursor-not-allowed"}`}>Call</a>
              <a href={(data.work_email || data.personal_email || data.email) ? `mailto:${data.work_email || data.personal_email || data.email}` : undefined} className={`px-3 py-2 rounded-lg border text-sm text-center ${(data.work_email || data.personal_email || data.email) ? "hover:bg-blue-50 hover:border-blue-200" : "text-gray-300 cursor-not-allowed"}`}>Email</a>
              <Link to={`/admin/employees/${employeeId}/edit`} className="px-3 py-2 rounded-lg border text-sm text-center hover:bg-blue-50 hover:border-blue-200">Edit Profile</Link>
              <a href={`/admin/employees/${employeeId}/id-card-print`} target="_blank" rel="noopener noreferrer" className={`px-3 py-2 rounded-lg border text-sm text-center ${data.photo_approved ? "hover:bg-blue-50 hover:border-blue-200" : "pointer-events-none text-gray-300"}`}>ID Card</a>
            </div>
          </div>
        </div>

        <div className="xl:col-span-5 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-start justify-between gap-3 mb-4"><div><h2 className="text-xl font-bold text-gray-900">Contact & Personal</h2><p className="text-xs text-gray-500">Identity, contact, and emergency information</p></div><StatusPill value={data.employment_status} /></div>
            <InfoRow label="Legal Name" value={`${data.salutation || ""} ${data.first_name || ""} ${data.middle_name || ""} ${data.last_name || ""} ${data.suffix || ""}`.replace(/\s+/g, " ").trim()} />
            <InfoRow label="Preferred Name" value={data.preferred_name} />
            <InfoRow label="Mobile Phone" value={data.phone} />
            <InfoRow label="Home Phone" value={data.home_phone} />
            <InfoRow label="Personal Email" value={data.personal_email} />
            <InfoRow label="Work Email" value={data.work_email || data.email} />
            <InfoRow label="Address" value={[data.address, data.city, data.state, data.zip].filter(Boolean).join(", ")} />
            <InfoRow label="Gender" value={data.gender} />
            <InfoRow label="Marital Status" value={data.marital_status} />
            <InfoRow label="Date of Birth" value={formatFriendlyDate(data.date_of_birth)} />
            <SecureSsnRow employeeId={employeeId} last4={data.ssn_last4} />
            <InfoRow label="Emergency Contact" value={data.next_of_kin ? `${data.next_of_kin}${data.next_of_kin_relationship ? ` · ${data.next_of_kin_relationship}` : ""} (${data.next_of_kin_phone || "—"})` : null} />
            <InfoRow label="Emergency Email" value={data.next_of_kin_email} />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="mb-3"><h3 className="font-semibold" style={{ color: "#154E93" }}>Employment Information</h3><p className="text-xs text-gray-500">Role, reporting structure, and compensation</p></div>
            <InfoRow label="Department" value={data.department} />
            <InfoRow label="Position" value={data.position} />
            <InfoRow label="Branch / Office" value={data.branch_office} />
            <InfoRow label="Manager" value={data.manager} />
            <InfoRow label="Time Tracking ID" value={data.time_tracking_id} />
            <InfoRow label="Probation Ends" value={formatFriendlyDate(data.probation_end)} />
            {/Licensed Nursing Assistant|\bLNA\b/i.test(`${data.caregiver_type || ""} ${data.position || ""}`) && <>
              <InfoRow label="NH LNA License" value={data.lna_license_number} />
              <InfoRow label="LNA License Expires" value={formatFriendlyDate(data.lna_license_expiration)} />
              <InfoRow label="LNA License Status" value={data.lna_license_status} />
            </>}
            {isFieldDuty ? <><InfoRow label="Caregiver Type" value={data.caregiver_type} /><InfoRow label="Hourly Pay Rate" value={data.hourly_pay_rate ? `$${data.hourly_pay_rate}/hr` : null} /><InfoRow label="Mileage Rate" value={data.mileage_rate ? `$${data.mileage_rate}/mi` : null} /></> : <><InfoRow label="Basic Salary" value={data.basic_salary ? `$${Number(data.basic_salary).toLocaleString()}` : null} /><InfoRow label="Commission Rate" value={data.commission_rate ? `${data.commission_rate}%` : null} /></>}
          </div>

          <OnboardingPanel tasks={onboarding} />
        </div>

        <div className="xl:col-span-4 space-y-4">
          <ActionPriorityPanel data={data} />
          <ReadinessPanel data={data} />
          <IdCardPreviewCard employeeId={employeeId} data={data} onChange={onChange} />
          <WorkforceInsights insights={data.insights || {}} />
          <RenewalsPanel data={data} />
          <CompliancePanel documents={data.documents || []} />
          <EmployeeTimeline employeeId={employeeId} initialEvents={data.timeline || []} />
        </div>
      </div>

      {isFieldDuty && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"><h3 className="font-semibold mb-2" style={{ color: "#154E93" }}>Transportation</h3><InfoRow label="Driver's License" value={data.driver_license_number ? `${data.driver_license_number} (${data.driver_license_state || "—"})` : null} /><InfoRow label="License Expires" value={formatFriendlyDate(data.driver_license_expiration)} /><InfoRow label="Has Vehicle" value={data.has_vehicle ? "Yes" : "No"} /><InfoRow label="Insurance Expires" value={formatFriendlyDate(data.vehicle_insurance_expiration)} /><InfoRow label="Travel Radius" value={data.travel_radius_miles ? `${data.travel_radius_miles} miles` : null} /></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"><h3 className="font-semibold mb-2" style={{ color: "#154E93" }}>Availability</h3>{["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((d) => <InfoRow key={d} label={d[0].toUpperCase() + d.slice(1)} value={data.availability?.[d] || null} />)}<InfoRow label="Max Weekly Hours" value={data.max_weekly_hours} /><InfoRow label="Live-in Available" value={data.live_in_available ? "Yes" : "No"} /><InfoRow label="Emergency Shifts" value={data.accepts_emergency_shifts ? "Yes" : "No"} /></div>
        </div>
      )}

      {(data.notes || data.remarks) && <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"><h3 className="font-semibold mb-2" style={{ color: "#154E93" }}>Notes</h3><p className="text-sm text-gray-700 whitespace-pre-wrap">{data.notes || data.remarks}</p></div>}
    </div>
  );
}

const TIMELINE_STYLES = {
  Employment: "bg-blue-100 text-blue-700",
  Onboarding: "bg-purple-100 text-purple-700",
  Identity: "bg-emerald-100 text-emerald-700",
  Profile: "bg-gray-100 text-gray-700",
  Exit: "bg-red-100 text-red-700",
  Note: "bg-amber-100 text-amber-700",
  General: "bg-slate-100 text-slate-700",
};

function formatTimelineDate(value) {
  if (!value) return "—";
  const date = new Date(String(value).includes("T") ? value : String(value).replace(" ", "T") + "Z");
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function EmployeeTimeline({ employeeId, initialEvents = [], fullView = false }) {
  const [events, setEvents] = useState(initialEvents);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ event_type: "Note", title: "", description: "", event_date: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadTimeline = () => api.employees.timeline.list(employeeId).then(setEvents).catch((e) => setError(e.message));
  useEffect(() => { setEvents(initialEvents); }, [initialEvents]);
  useEffect(() => { if (fullView) loadTimeline(); }, [employeeId, fullView]);

  const addEvent = async () => {
    if (!form.title.trim()) return;
    setSaving(true); setError("");
    try {
      await api.employees.timeline.add(employeeId, { ...form, event_date: form.event_date || null });
      setForm({ event_type: "Note", title: "", description: "", event_date: "" });
      setShowAdd(false);
      await loadTimeline();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const visibleEvents = fullView ? events : events.slice(0, 5);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold" style={{ color: "#154E93" }}>Employee Timeline</h3>
          <p className="text-xs text-gray-500">Chronological employment history</p>
        </div>
        <button onClick={() => setShowAdd((v) => !v)} className="px-2.5 py-1.5 rounded-lg border text-xs hover:bg-gray-50">
          {showAdd ? "Cancel" : "+ Add Event"}
        </button>
      </div>

      {error && <div className="mb-3 text-xs bg-red-50 text-red-700 px-3 py-2 rounded-lg">{error}</div>}
      {showAdd && (
        <div className="mb-4 p-3 rounded-lg border bg-gray-50 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select className="border rounded-lg px-2 py-1.5 text-sm" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
              {["Note", "Employment", "Onboarding", "Identity", "Performance", "Training", "Compliance", "Scheduling", "Payroll", "General"].map((x) => <option key={x}>{x}</option>)}
            </select>
            <input type="datetime-local" className="border rounded-lg px-2 py-1.5 text-sm" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
          </div>
          <input className="w-full border rounded-lg px-2 py-1.5 text-sm" placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea rows={2} className="w-full border rounded-lg px-2 py-1.5 text-sm" placeholder="Details (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button disabled={saving || !form.title.trim()} onClick={addEvent} className="px-3 py-1.5 rounded-lg text-white text-sm disabled:opacity-50" style={{ backgroundColor: "#154E93" }}>
            {saving ? "Saving…" : "Save Event"}
          </button>
        </div>
      )}

      <div className="relative">
        {visibleEvents.length === 0 ? (
          <p className="text-sm text-gray-400 py-3">No timeline events yet.</p>
        ) : visibleEvents.map((event, index) => (
          <div key={event.id} className="relative pl-8 pb-5 last:pb-0">
            {index < visibleEvents.length - 1 && <span className="absolute left-[13px] top-6 bottom-0 w-px bg-gray-200" />}
            <span className={`absolute left-0 top-0.5 w-7 h-7 rounded-full border-4 border-white shadow-sm inline-flex items-center justify-center text-[10px] font-bold ${TIMELINE_STYLES[event.event_type] || TIMELINE_STYLES.General}`}>{({ Employment: "E", Onboarding: "O", Identity: "ID", Performance: "★", Training: "T", Compliance: "✓", Scheduling: "S", Payroll: "$", Exit: "×", Note: "N" })[event.event_type] || "•"}</span>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TIMELINE_STYLES[event.event_type] || TIMELINE_STYLES.General}`}>{event.event_type}</span>
              <span className="text-[11px] text-gray-400">{formatTimelineDate(event.event_date)}</span>
            </div>
            <div className="text-sm font-semibold text-gray-800">{event.title}</div>
            {event.description && <div className="text-xs text-gray-500 mt-0.5 whitespace-pre-wrap">{event.description}</div>}
            {(event.source || event.created_by) && <div className="text-[10px] text-gray-400 mt-1">{event.source || "HR"}{event.created_by ? ` · ${event.created_by}` : ""}</div>}
          </div>
        ))}
      </div>
      {!fullView && events.length > 5 && <p className="text-xs text-gray-400 mt-3">Open the History tab to view all {events.length} events.</p>}
    </div>
  );
}

function SkillsTab({ employeeId, skills, onChange }) {
  const emptyForm = {
    skill_name: "", experience_years: "", competency_level: "Developing",
    assessment_method: "Observation", last_assessed_date: "", next_review_date: "",
    competency_verified: false, status: "Active", notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const active = skills.filter((s) => (s.status || "Active") === "Active");
  const verifiedCount = active.filter((s) => Number(s.competency_verified) === 1).length;
  const reviewDue = active.filter((s) => s.next_review_date && s.next_review_date <= today).length;
  const verifiedPct = active.length ? Math.round((verifiedCount / active.length) * 100) : 0;

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const reset = () => { setForm(emptyForm); setEditingId(null); setError(""); };
  const edit = (skill) => {
    setEditingId(skill.id);
    setForm({
      skill_name: skill.skill_name || "",
      experience_years: skill.experience_years ?? "",
      competency_level: skill.competency_level || "Developing",
      assessment_method: skill.assessment_method || "Observation",
      last_assessed_date: skill.last_assessed_date || "",
      next_review_date: skill.next_review_date || "",
      competency_verified: Number(skill.competency_verified) === 1,
      status: skill.status || "Active",
      notes: skill.notes || "",
    });
    window.scrollTo({ top: 180, behavior: "smooth" });
  };
  const save = async () => {
    if (!form.skill_name) return setError("Select a skill before saving.");
    setSaving(true); setError("");
    try {
      if (editingId) await api.caregivers.skills.update(editingId, form);
      else await api.caregivers.skills.add(employeeId, form);
      reset(); await onChange();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this skill record?")) return;
    await api.caregivers.skills.remove(id); onChange();
  };
  const levelClass = (level) => ({
    "Independent": "bg-green-50 text-green-700",
    "Competent": "bg-blue-50 text-blue-700",
    "Developing": "bg-yellow-50 text-yellow-700",
    "Needs Training": "bg-red-50 text-red-700",
  }[level] || "bg-gray-100 text-gray-600");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricCard label="Skills Recorded" value={skills.length} sub="Employee capability profile" color="blue" />
        <MetricCard label="Verified Competencies" value={verifiedCount} sub={`${verifiedPct}% of active skills`} color="green" />
        <MetricCard label="Reviews Due" value={reviewDue} sub="Assessment date reached" color={reviewDue ? "red" : "green"} />
        <MetricCard label="Needs Training" value={active.filter((s) => s.competency_level === "Needs Training").length} sub="Development plan required" color="amber" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 text-white" style={{ background: "linear-gradient(110deg,#123a78,#1f5db4)" }}>
          <div className="font-semibold">{editingId ? "Update Competency Record" : "Add Skill & Competency"}</div>
          <div className="text-xs text-blue-100 mt-1">Track experience, assessment, verification, and reassessment dates.</div>
        </div>
        <div className="p-5">
          {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded-lg">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Skill">
              <select value={form.skill_name} disabled={Boolean(editingId)} onChange={(e) => setField("skill_name", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-100">
                <option value="">Select required skill…</option>
                {SKILL_OPTIONS.map((skill) => <option key={skill}>{skill}</option>)}
              </select>
            </Field>
            <Field label="Experience (years)"><input type="number" min="0" step="0.5" value={form.experience_years} onChange={(e) => setField("experience_years", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Competency Level">
              <select value={form.competency_level} onChange={(e) => setField("competency_level", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                {['Needs Training','Developing','Competent','Independent'].map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Assessment Method">
              <select value={form.assessment_method} onChange={(e) => setField("assessment_method", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                {['Observation','Skills Demonstration','Written Test','Certificate Review','Supervisor Review'].map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Last Assessed"><input type="date" value={form.last_assessed_date} onChange={(e) => setField("last_assessed_date", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Next Review"><input type="date" value={form.next_review_date} onChange={(e) => setField("next_review_date", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Record Status">
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white"><option>Active</option><option>Inactive</option></select>
            </Field>
            <label className="flex items-center gap-2 mt-6 text-sm font-medium text-gray-700"><input type="checkbox" checked={form.competency_verified} onChange={(e) => setField("competency_verified", e.target.checked)} /> Competency verified</label>
          </div>
          <div className="mt-4"><Field label="Assessment Notes"><textarea rows="2" value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Observed strengths, limitations, retraining needs, or supervisor comments" className="w-full border rounded-lg px-3 py-2 text-sm" /></Field></div>
          <div className="mt-4 flex justify-end gap-2">
            {editingId && <button onClick={reset} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>}
            <button onClick={save} disabled={saving} className="px-5 py-2 text-sm text-white rounded-lg disabled:opacity-60" style={{ backgroundColor: "#154E93" }}>{saving ? "Saving…" : editingId ? "Save Changes" : "Add Competency"}</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div><h3 className="font-semibold text-gray-900">Competency Matrix</h3><p className="text-xs text-gray-500 mt-0.5">Use this record for caregiver matching, supervision, and annual competency review.</p></div>
          <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">{skills.length} records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-400 bg-gray-50 border-b"><th className="px-5 py-3">Skill</th><th>Experience</th><th>Level</th><th>Assessment</th><th>Review</th><th>Verification</th><th>Status</th><th className="pr-5 text-right">Actions</th></tr></thead>
            <tbody>
              {skills.map((skill) => {
                const due = skill.next_review_date && skill.next_review_date <= today;
                return <tr key={skill.id} className="border-b last:border-0 hover:bg-gray-50 align-top">
                  <td className="px-5 py-4"><div className="font-medium text-gray-900">{skill.skill_name}</div>{skill.notes && <div className="text-xs text-gray-500 mt-1 max-w-xs">{skill.notes}</div>}</td>
                  <td className="py-4">{skill.experience_years !== null && skill.experience_years !== '' ? `${skill.experience_years} yrs` : "—"}</td>
                  <td className="py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${levelClass(skill.competency_level)}`}>{skill.competency_level || "Developing"}</span></td>
                  <td className="py-4"><div>{skill.assessment_method || "—"}</div><div className="text-xs text-gray-400 mt-1">{skill.last_assessed_date || "Not assessed"}</div></td>
                  <td className="py-4"><div className={due ? "text-red-600 font-medium" : "text-gray-700"}>{skill.next_review_date || "Not scheduled"}</div>{due && <div className="text-[11px] text-red-500 mt-1">Review due</div>}</td>
                  <td className="py-4">{Number(skill.competency_verified) === 1 ? <><span className="text-green-700 font-medium">Verified</span><div className="text-xs text-gray-400 mt-1">{skill.verified_by || "HR"}</div></> : <span className="text-yellow-700">Pending</span>}</td>
                  <td className="py-4"><span className={`px-2 py-1 rounded-full text-xs ${skill.status === 'Inactive' ? 'bg-gray-100 text-gray-500' : 'bg-green-50 text-green-700'}`}>{skill.status || "Active"}</span></td>
                  <td className="py-4 pr-5 text-right whitespace-nowrap"><button onClick={() => edit(skill)} className="text-blue-700 hover:underline text-xs mr-3">Edit</button><button onClick={() => remove(skill.id)} className="text-red-600 hover:underline text-xs">Remove</button></td>
                </tr>;
              })}
              {skills.length === 0 && <tr><td colSpan="8" className="py-12 text-center text-gray-400">No skills or competency assessments recorded.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function ComplianceCenter({ employeeId, employee, onDocumentsChange, onOpenDocuments }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  const [sending, setSending] = useState(false);
  const [scanRunning, setScanRunning] = useState(false);
  const [selectedRequirementKeys, setSelectedRequirementKeys] = useState([]);
  const [notice, setNotice] = useState("");
  const [inspectionMode, setInspectionMode] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [recipient, setRecipient] = useState(employee.work_email || employee.personal_email || employee.email || "");
  const [subject, setSubject] = useState("Action Required: Jybek HomeCare Services compliance items");
  const [message, setMessage] = useState("");

  const load = () => api.employees.compliance.get(employeeId).then((result) => {
    setData(result);
    const attention = result.requirements.filter((r) => ["Missing", "Expired", "Rejected", "Expiring Soon"].includes(r.status));
    const name = employee.preferred_name || employee.first_name || "Employee";
    setMessage(`Hello ${name},\n\nYour Jybek HomeCare Services personnel file requires attention.\n\n${attention.map((r, i) => `${i + 1}. ${r.label} — ${r.status}${r.detail ? ` (${r.detail})` : ""}`).join("\n")}\n\nPlease submit or upload the required evidence as soon as possible. Contact Human Resources if you need assistance.\n\nThank you,\nHuman Resources\nJybek HomeCare Services`);
    setOpenGroups((prev) => Object.keys(prev).length ? prev : Object.fromEntries([...new Set(result.requirements.map((r) => r.category))].map((c) => [c, true])));
  }).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [employeeId]);

  const sendReminder = async () => {
    setSending(true); setError(""); setNotice("");
    try {
      await api.employees.compliance.sendReminder(employeeId, { recipient, subject, message, requirement_keys: selectedRequirementKeys });
      setNotice("Compliance reminder sent successfully.");
      setShowCompose(false);
      setSelectedRequirementKeys([]);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };


  const composeForItem = (item) => {
    const name = employee.preferred_name || employee.first_name || "Employee";
    setSelectedRequirementKeys([item.key]);
    setSubject(`Action Required: ${item.label}`);
    setMessage(`Hello ${name},

Your Jybek HomeCare Services personnel file requires the following item:

${item.label} — ${item.status}${item.due_date ? `
Due date: ${item.due_date}` : ""}${item.source ? `
Requirement source: ${item.source}` : ""}

Please log in to your employee portal and upload the required evidence. Contact Human Resources if you need assistance.

Thank you,
Human Resources
Jybek HomeCare Services`);
    setShowCompose(true);
  };

  const runReminderScan = async () => {
    setScanRunning(true); setError(""); setNotice("");
    try {
      const result = await api.employees.compliance.runReminderScan(false);
      setNotice(`Reminder scan complete: ${result.sent} email${result.sent === 1 ? "" : "s"} sent, ${result.skipped} skipped, ${result.failed} failed.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setScanRunning(false); }
  };

  if (!data) return <div className="text-gray-500">Loading compliance center…</div>;
  const statusClass = (status) => ({
    Verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Pending Review": "bg-blue-50 text-blue-700 border-blue-200",
    "Expiring Soon": "bg-amber-50 text-amber-700 border-amber-200",
    Missing: "bg-red-50 text-red-700 border-red-200",
    Expired: "bg-red-50 text-red-700 border-red-200",
    Rejected: "bg-rose-50 text-rose-700 border-rose-200",
  }[status] || "bg-gray-50 text-gray-600 border-gray-200");
  const statusDot = (status) => ({ Verified: "bg-emerald-500", "Pending Review": "bg-blue-500", "Expiring Soon": "bg-amber-500", Missing: "bg-red-500", Expired: "bg-red-700", Rejected: "bg-rose-700" }[status] || "bg-gray-400");
  const grouped = data.requirements.reduce((acc, item) => { (acc[item.category] ||= []).push(item); return acc; }, {});
  const categoryStats = Object.fromEntries((data.category_scores || []).map((row) => [row.category, row]));
  const attentionItems = data.requirements.filter((r) => ["Missing", "Expired", "Rejected", "Expiring Soon"].includes(r.status));
  const readinessTone = data.score >= 90 ? "emerald" : data.score >= 70 ? "amber" : "red";
  const readinessLabel = data.score >= 90 ? "Inspection Ready" : data.score >= 70 ? "Minor Deficiencies" : "Action Required";
  const toggleGroup = (category) => setOpenGroups((prev) => ({ ...prev, [category]: !prev[category] }));

  if (inspectionMode) return <div className="bg-white rounded-xl border shadow-sm overflow-hidden print:border-0 print:shadow-none">
    <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3 print:hidden">
      <div><h2 className="font-bold text-xl text-blue-950">Inspection Mode</h2><p className="text-xs text-gray-500">Clean personnel-file evidence view for surveyors and audits.</p></div>
      <div className="flex gap-2"><button onClick={() => window.print()} className="px-4 py-2 rounded-lg border text-sm font-semibold">Print / Save PDF</button><button onClick={() => setInspectionMode(false)} className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: "#154E93" }}>Exit Inspection Mode</button></div>
    </div>
    <div className="p-6">
      <div className="flex justify-between gap-4 border-b pb-4 mb-4"><div><div className="text-xs uppercase tracking-widest text-gray-500">Jybek HomeCare Services</div><h1 className="text-2xl font-bold text-blue-950">Employee Personnel Compliance File</h1><p className="text-sm text-gray-600">{employee.first_name} {employee.last_name} · {employee.employee_id} · {employee.position || employee.caregiver_type || employee.staff_category}</p></div><div className="text-right"><div className="text-3xl font-bold text-blue-900">{data.score}%</div><div className="text-xs text-gray-500">Inspection readiness</div></div></div>
      <table className="w-full text-sm border-collapse">
        <thead><tr className="bg-gray-50 text-left"><th className="p-3 border">Category</th><th className="p-3 border">Requirement</th><th className="p-3 border">Status</th><th className="p-3 border">Evidence</th><th className="p-3 border">Details</th></tr></thead>
        <tbody>{data.requirements.map((item) => <tr key={item.key}><td className="p-3 border">{item.category}</td><td className="p-3 border font-medium">{item.label}</td><td className="p-3 border">{item.status}</td><td className="p-3 border">{item.document?.id ? <a className="text-blue-700 underline print:text-black print:no-underline" href={api.caregivers.documents.fileUrl(item.document.id)} target="_blank" rel="noreferrer">Open evidence</a> : "Not attached"}</td><td className="p-3 border text-gray-600">{item.detail || "—"}</td></tr>)}</tbody>
      </table>
    </div>
  </div>;

  return <div className="space-y-4">
    {error && <div className="rounded-lg bg-red-50 text-red-700 border border-red-200 px-4 py-3 text-sm">{error}</div>}
    {notice && <div className="rounded-lg bg-green-50 text-green-700 border border-green-200 px-4 py-3 text-sm">{notice}</div>}

    <div className="rounded-2xl bg-gradient-to-r from-blue-950 via-blue-800 to-blue-700 text-white p-5 shadow-sm">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
        <div>
          <div className="text-xs uppercase tracking-[.18em] text-blue-200 font-semibold">NH DHHS Employee Compliance</div>
          <h2 className="text-2xl font-bold mt-1">Inspection Readiness</h2>
          <p className="text-sm text-blue-100 mt-1">Role-based compliance, evidence, reminders, and scheduling readiness in one workspace.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
            <div className="absolute inset-2 rounded-full border-[7px] border-white/20" />
            <span className="text-2xl font-bold">{data.score}%</span>
          </div>
          <div>
            <div className={`inline-flex px-3 py-1 rounded-full text-sm font-bold ${readinessTone === "emerald" ? "bg-emerald-400/20 text-emerald-100" : readinessTone === "amber" ? "bg-amber-300/20 text-amber-100" : "bg-red-400/20 text-red-100"}`}>{readinessLabel}</div>
            <div className={`mt-2 inline-flex px-3 py-1 rounded-full text-xs font-semibold ${data.ready_for_scheduling ? "bg-emerald-400/20 text-emerald-100" : "bg-red-400/20 text-red-100"}`}>{data.ready_for_scheduling ? "Ready for Scheduling" : "Not Ready for Scheduling"}</div>
            <div className="text-xs text-blue-100 mt-2">{data.blockers} blocking · {data.needs_attention} need attention</div>
          </div>
        </div>
      </div>
      <div className="h-2 bg-white/15 rounded-full mt-5 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-cyan-300 to-emerald-300" style={{ width: `${data.score}%` }} /></div>
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MetricCard label="Requirements" value={data.total} helper="Role-based checklist" tone="blue" icon="≡" />
      <MetricCard label="Verified" value={data.verified} helper="Approved by HR" tone="green" icon="✓" progress={data.verified_score} />
      <MetricCard label="Blocking" value={data.blockers} helper="Prevents scheduling readiness" tone="amber" icon="!" />
      <MetricCard label="Attention" value={data.needs_attention} helper="Missing, expired or due soon" tone="purple" icon="⌁" />
    </div>

    <div className="bg-white rounded-xl border shadow-sm p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
      <div>
        <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${data.auto_reminders_enabled ? "bg-emerald-500" : "bg-gray-400"}`} /><h3 className="font-semibold text-blue-950">Smart Reminder Engine</h3></div>
        <p className="text-xs text-gray-500 mt-1">{data.auto_reminders_enabled ? "Automatic daily scans are enabled." : "Automatic scans are off. Run a scan manually or enable COMPLIANCE_AUTO_REMINDERS=true in .env."}</p>
      </div>
      <button disabled={scanRunning} onClick={runReminderScan} className="px-4 py-2 rounded-lg border border-blue-200 text-sm font-semibold text-blue-800 hover:bg-blue-50 disabled:opacity-50">{scanRunning ? "Scanning…" : "Run Reminder Scan"}</button>
    </div>

    <div className="bg-white rounded-xl border shadow-sm p-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div><h3 className="font-semibold text-blue-950">Compliance Actions</h3><p className="text-xs text-gray-500">Resolve deficiencies without leaving the employee workspace.</p></div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onOpenDocuments} className="px-4 py-2 rounded-lg border text-sm font-semibold text-blue-800 border-blue-200 hover:bg-blue-50">Upload Evidence</button>
          <button onClick={() => setShowCompose(true)} className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: "#154E93" }}>Compose Reminder</button>
          <button onClick={() => setInspectionMode(true)} className="px-4 py-2 rounded-lg border text-sm font-semibold text-gray-700 hover:bg-gray-50">Inspection Mode</button>
          <button onClick={() => { onDocumentsChange(); load(); }} className="px-4 py-2 rounded-lg border text-sm font-semibold text-gray-700 hover:bg-gray-50">Refresh</button>
        </div>
      </div>
      {attentionItems.length > 0 && <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">{attentionItems.slice(0, 6).map((item) => <button key={item.key} onClick={onOpenDocuments} className="text-left rounded-lg border border-red-100 bg-red-50/60 p-3 hover:bg-red-50"><div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${statusDot(item.status)}`} /><span className="font-semibold text-sm text-gray-900">{item.label}</span></div><div className="text-xs text-gray-500 mt-1">{item.status}{item.detail ? ` · ${item.detail}` : ""}</div><div className="text-[11px] text-blue-700 font-semibold mt-2">Upload or replace evidence →</div></button>)}</div>}
    </div>

    {showCompose && <div className="bg-white rounded-xl border shadow-sm p-5">
      <div className="flex justify-between items-start gap-3 mb-4"><div><h3 className="font-semibold text-blue-900">Compose Compliance Reminder</h3><p className="text-xs text-gray-500">{selectedRequirementKeys.length === 1 ? "Reminder for one selected requirement." : "Pre-filled from the employee's current missing and expiring requirements."}</p></div><button onClick={() => { setShowCompose(false); setSelectedRequirementKeys([]); }} className="text-gray-400">✕</button></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3"><label className="text-xs font-semibold text-gray-600">Recipient<input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={recipient} onChange={(e) => setRecipient(e.target.value)} /></label><label className="text-xs font-semibold text-gray-600">Subject<input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={subject} onChange={(e) => setSubject(e.target.value)} /></label></div>
      <label className="block text-xs font-semibold text-gray-600 mt-3">Message<textarea className="mt-1 w-full border rounded-lg px-3 py-2 text-sm min-h-[220px]" value={message} onChange={(e) => setMessage(e.target.value)} /></label>
      <div className="flex justify-end gap-2 mt-3"><button onClick={() => { setShowCompose(false); setSelectedRequirementKeys([]); }} className="px-4 py-2 rounded-lg border text-sm">Cancel</button><button disabled={sending || !recipient || !subject || !message} onClick={sendReminder} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: "#154E93" }}>{sending ? "Sending…" : "Send Email"}</button></div>
    </div>}

    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {Object.entries(grouped).map(([category, items]) => {
        const stats = categoryStats[category];
        const isOpen = openGroups[category] !== false;
        return <div key={category} className="bg-white rounded-xl border shadow-sm overflow-hidden self-start">
          <button onClick={() => toggleGroup(category)} className="w-full px-4 py-3 border-b bg-gray-50 hover:bg-gray-100 flex items-center justify-between gap-3 text-left">
            <div><h3 className="font-semibold text-blue-900">{category}</h3><p className="text-xs text-gray-500">{stats.compliant} of {stats.total} compliant · {stats.verified} verified</p></div>
            <div className="flex items-center gap-3"><div className="w-28 h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${stats.score}%` }} /></div><span className="text-xs font-bold text-gray-700 w-9 text-right">{stats.score}%</span><span className="text-gray-400">{isOpen ? "−" : "+"}</span></div>
          </button>
          {isOpen && <div className="divide-y">{items.map((item) => <div key={item.key} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0"><div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${statusDot(item.status)}`} /><div className="font-semibold text-sm text-gray-900">{item.label}</div></div><div className="text-xs text-gray-500 mt-1 ml-4">{item.detail || "No evidence has been attached."}</div><div className="flex flex-wrap gap-2 mt-2 ml-4"><span className="text-[10px] uppercase font-semibold tracking-wide text-gray-400">{item.frequency}</span>{item.blocks && <span className="text-[10px] uppercase font-semibold tracking-wide text-red-500">Scheduling blocker</span>}{item.due_date && <span className="text-[10px] font-semibold text-amber-700">Due {item.due_date}{item.days_remaining !== null ? ` · ${item.days_remaining < 0 ? `${Math.abs(item.days_remaining)} days overdue` : `${item.days_remaining} days left`}` : ""}</span>}{item.effective && <span className="text-[10px] text-blue-600">Effective {item.effective}</span>}</div>{item.source && <div className="text-[11px] text-gray-400 mt-2 ml-4">Source: {item.source}</div>}</div>
              <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-semibold shrink-0 ${statusClass(item.status)}`}>{item.status}</span>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 ml-4">{item.document?.id ? <a className="text-xs font-semibold text-blue-700 hover:underline" href={api.caregivers.documents.fileUrl(item.document.id)} target="_blank" rel="noreferrer">Open evidence</a> : <button onClick={onOpenDocuments} className="text-xs font-semibold text-blue-700 hover:underline">Upload evidence</button>}<button onClick={() => composeForItem(item)} className="text-xs font-semibold text-gray-600 hover:underline">Send item reminder</button></div>
          </div>)}</div>}
        </div>;
      })}
    </div>

    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b"><h3 className="font-semibold text-blue-900">Reminder History</h3><p className="text-xs text-gray-500">Audit trail of compliance communications</p></div>
      {data.reminders.length ? <div className="divide-y">{data.reminders.map((r) => <div key={r.id} className="px-4 py-3 flex justify-between gap-4 text-sm"><div><div className="font-medium">{r.subject}</div><div className="text-xs text-gray-500">To {r.recipient} · {formatTimelineDate(r.sent_at)}</div></div><StatusPill value={r.delivery_status} /></div>)}</div> : <div className="p-6 text-center text-sm text-gray-400">No compliance reminders sent yet.</div>}
    </div>
  </div>;
}

function DocumentsTab({ employeeId, documents, onChange }) {
  const [docType, setDocType] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expDate, setExpDate] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setDocType(""); setIssueDate(""); setExpDate(""); setNotes(""); setFile(null);
    const input = document.getElementById(`employee-document-file-${employeeId}`);
    if (input) input.value = "";
  };

  const add = async () => {
    if (!docType) return setError("Select a document type.");
    if (!file) return setError("Select a PDF, Word document, or image to upload.");
    setUploading(true); setError("");
    try {
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("The selected file could not be read."));
        reader.readAsDataURL(file);
      });
      await api.caregivers.documents.upload(employeeId, {
        document_type: docType,
        issue_date: issueDate || null,
        expiration_date: expDate || null,
        notes: notes || null,
        file_name: file.name,
        mime_type: file.type || "application/octet-stream",
        file_data: fileData,
      });
      reset();
      await onChange();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const markVerified = async (id, status) => {
    await api.caregivers.documents.update(id, { verified_status: status });
    onChange();
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name || "this document"}? The attached file will also be deleted.`)) return;
    await api.caregivers.documents.remove(id);
    onChange();
  };
  const fileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const attached = documents.filter((d) => d.stored_filename).length;
  const verified = documents.filter((d) => d.verified_status === "Verified").length;
  const expiring = documents.filter((d) => ["Expired", "Expiring Soon"].includes(docStatus(d).label)).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MetricCard label="Personnel Records" value={documents.length} helper={`${attached} with evidence attached`} tone="blue" icon="▣" />
        <MetricCard label="Verified" value={verified} helper="Reviewed by authorized HR staff" tone="green" icon="✓" />
        <MetricCard label="Needs Attention" value={expiring} helper="Expired or expiring within 30 days" tone="amber" icon="!" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-blue-950 to-blue-800 text-white">
          <h3 className="font-semibold">Upload to Digital Personnel File</h3>
          <p className="text-xs text-blue-200 mt-1">Files are securely stored, indexed, and linked to compliance and employee history.</p>
        </div>
        {error && <div className="mx-5 mt-4 text-sm bg-red-50 text-red-700 border border-red-100 px-3 py-2 rounded-lg">{error}</div>}
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 items-end">
          <div className="xl:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Document Type *</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Select required record…</option>
              {DOCUMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Issue / Completion Date</label>
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Expiration / Next Due</label>
            <input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Evidence File *</label>
            <input id={`employee-document-file-${employeeId}`} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-xs file:mr-2 file:rounded-md file:border-0 file:bg-blue-50 file:px-2 file:py-2 file:text-blue-700" />
          </div>
          <div className="md:col-span-2 xl:col-span-4">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Verification source, registry result, restrictions, or other notes" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <button disabled={uploading} onClick={add} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: "#154E93" }}>
            {uploading ? "Uploading…" : "Upload & Index"}
          </button>
        </div>
        <div className="px-5 pb-4 text-[11px] text-gray-400">Accepted: PDF, Word, JPG, PNG, WebP · Maximum 15 MB. Health-related records should be accessed only by authorized personnel.</div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div><h3 className="font-semibold" style={{ color: "#154E93" }}>Digital Personnel File</h3><p className="text-xs text-gray-500">Open, download, verify, and retrieve supporting evidence without searching folders.</p></div>
          <span className="text-xs rounded-full bg-blue-50 text-blue-700 px-3 py-1 font-semibold">{documents.length} record{documents.length === 1 ? "" : "s"}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50"><th className="px-5 py-3">Document</th><th>Dates</th><th>Validity</th><th>Verification</th><th>Evidence</th><th className="pr-5 text-right">Actions</th></tr></thead>
            <tbody>
              {documents.map((d) => {
                const status = docStatus(d);
                return (
                  <tr key={d.id} className="border-t border-gray-100 hover:bg-blue-50/30">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-gray-800">{d.document_type}</div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{d.original_filename || (d.file_link ? "External link" : "Metadata only")}{d.file_size ? ` · ${fileSize(d.file_size)}` : ""}</div>
                      {d.notes && <div className="text-[11px] text-gray-500 mt-1 max-w-sm truncate" title={d.notes}>{d.notes}</div>}
                    </td>
                    <td><div className="text-xs text-gray-700">Issued: {d.issue_date ? formatFriendlyDate(d.issue_date) : "—"}</div><div className="text-xs text-gray-500 mt-1">Due: {d.expiration_date ? formatFriendlyDate(d.expiration_date) : "No expiration"}</div></td>
                    <td><span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] ${status.label === "Valid" || status.label === "No Expiration" ? "bg-green-50 text-green-700" : status.label === "Expiring Soon" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{status.label}</span></td>
                    <td>
                      <select value={d.verified_status || "Pending"} onChange={(e) => markVerified(d.id, e.target.value)} className={`border rounded-lg px-2 py-1.5 text-xs ${d.verified_status === "Verified" ? "border-green-200 bg-green-50 text-green-700" : d.verified_status === "Rejected" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                        <option>Pending</option><option>Verified</option><option>Rejected</option>
                      </select>
                      {d.verified_by && <div className="text-[10px] text-gray-400 mt-1">By {d.verified_by}</div>}
                    </td>
                    <td>{d.stored_filename ? <div><div className="text-xs font-medium text-green-700">✓ File attached</div><div className="text-[10px] text-gray-400 mt-1">Uploaded {d.uploaded_at ? formatFriendlyDate(d.uploaded_at) : ""}</div></div> : <div className="text-xs text-amber-600">Evidence not attached</div>}</td>
                    <td className="pr-5 text-right whitespace-nowrap">
                      {d.stored_filename && <><a href={api.caregivers.documents.fileUrl(d.id)} target="_blank" rel="noreferrer" className="inline-flex px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 text-xs font-medium mr-1">Open</a><a href={api.caregivers.documents.fileUrl(d.id, true)} className="inline-flex px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium mr-1">Download</a></>}
                      <button onClick={() => remove(d.id, d.document_type)} className="inline-flex px-2.5 py-1.5 rounded-lg border border-red-100 text-red-600 text-xs">Remove</button>
                    </td>
                  </tr>
                );
              })}
              {documents.length === 0 && <tr><td colSpan={6} className="py-12 text-center"><div className="text-3xl mb-2">▣</div><div className="font-medium text-gray-500">No personnel documents uploaded</div><div className="text-xs text-gray-400 mt-1">Use the secure uploader above to create the employee's digital personnel file.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AssignmentsTab({ employeeId, assignments, onChange }) {
  const emptyForm = {
    client_id: "", start_date: new Date().toISOString().slice(0, 10), assignment_type: "Primary",
    service_type: "", priority: "Standard", weekly_hours: "", schedule_pattern: "",
    compatibility_status: "Pending Review", notes: ""
  };
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("Active");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ending, setEnding] = useState(null);
  const [endReason, setEndReason] = useState("");

  useEffect(() => { api.clients.list().then(setClients).catch((e) => setError(e.message)); }, []);

  const selectedClient = clients.find((c) => c.client_id === form.client_id);
  const active = assignments.filter((a) => a.status === "Active");
  const pendingReview = active.filter((a) => a.compatibility_status === "Pending Review").length;
  const weeklyHours = active.reduce((sum, a) => sum + (Number(a.weekly_hours) || 0), 0);
  const visible = assignments.filter((a) => {
    const matchesStatus = filter === "All" || a.status === filter;
    const text = `${a.client_first_name || ""} ${a.client_last_name || ""} ${a.client_id || ""}`.toLowerCase();
    return matchesStatus && text.includes(query.toLowerCase());
  });

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const add = async () => {
    if (!form.client_id) return setError("Select a client before creating the assignment.");
    setSaving(true); setError("");
    try {
      await api.caregivers.assignments.add(employeeId, form);
      setForm(emptyForm);
      await onChange();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const update = async (id, data) => {
    try { await api.caregivers.assignments.update(id, data); await onChange(); }
    catch (e) { setError(e.message); }
  };

  const finishEnd = async () => {
    if (!ending) return;
    await update(ending.id, { status: "Ended", end_date: new Date().toISOString().slice(0, 10), end_reason: endReason || "Assignment ended by HR" });
    setEnding(null); setEndReason("");
  };

  return (
    <div className="space-y-4">
      {error && <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetricCard label="Active Clients" value={active.length} helper="Current caregiver assignments" tone="blue" icon="⌂" />
        <MetricCard label="Weekly Hours" value={weeklyHours || 0} helper="Planned assignment hours" tone="green" icon="◷" />
        <MetricCard label="Compatibility Review" value={pendingReview} helper="Awaiting match confirmation" tone="amber" icon="◇" />
        <MetricCard label="Assignment History" value={assignments.length} helper="Active and ended records" tone="purple" icon="↺" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-[#12377A] to-[#1957B8] text-white">
          <h3 className="font-semibold">Create Client Assignment</h3>
          <p className="text-xs text-blue-100 mt-1">Connect this caregiver to an active client and record the operational match.</p>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Field label="Client *">
            <select value={form.client_id} onChange={(e) => setField("client_id", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Select client…</option>
              {clients.filter((c) => c.status !== "Discharged").map((c) => <option key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name} · {c.client_id}</option>)}
            </select>
          </Field>
          <Field label="Assignment Type">
            <select value={form.assignment_type} onChange={(e) => setField("assignment_type", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
              {['Primary','Secondary','Backup','Temporary','Respite'].map((v) => <option key={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Service Type">
            <input value={form.service_type} onChange={(e) => setField("service_type", e.target.value)} placeholder={selectedClient?.care_type || "Personal care, homemaking…"} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Start Date">
            <input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Priority">
            <select value={form.priority} onChange={(e) => setField("priority", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
              {['Standard','High','Urgent'].map((v) => <option key={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Weekly Hours">
            <input type="number" min="0" step="0.25" value={form.weekly_hours} onChange={(e) => setField("weekly_hours", e.target.value)} placeholder="0" className="w-full border rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Schedule Pattern">
            <input value={form.schedule_pattern} onChange={(e) => setField("schedule_pattern", e.target.value)} placeholder="Mon–Fri, 8 AM–12 PM" className="w-full border rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Compatibility">
            <select value={form.compatibility_status} onChange={(e) => setField("compatibility_status", e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
              {['Pending Review','Good Match','Trial Match','Needs Review'].map((v) => <option key={v}>{v}</option>)}
            </select>
          </Field>
          <div className="md:col-span-3">
            <Field label="Assignment Notes"><input value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Client preferences, compatibility details, travel considerations, or supervisor instructions" className="w-full border rounded-lg px-3 py-2 text-sm" /></Field>
          </div>
          <div className="flex items-end"><button disabled={saving || !form.client_id} onClick={add} className="w-full px-4 py-2.5 rounded-lg bg-[#154E93] text-white text-sm font-semibold disabled:opacity-50">{saving ? "Assigning…" : "Create Assignment"}</button></div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 border-b">
          <div><h3 className="font-semibold text-[#154E93]">Client Assignment Register</h3><p className="text-xs text-gray-500">Operational assignment, compatibility, hours, and service history.</p></div>
          <div className="flex gap-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search client…" className="border rounded-lg px-3 py-2 text-sm" />
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm"><option>Active</option><option>Pending</option><option>Ended</option><option>All</option></select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50"><th className="px-4 py-3">Client</th><th>Assignment</th><th>Schedule</th><th>Compatibility</th><th>Status</th><th className="text-right px-4">Actions</th></tr></thead>
            <tbody>
              {visible.map((a) => (
                <tr key={a.id} className="border-t align-top hover:bg-gray-50">
                  <td className="px-4 py-4"><div className="font-semibold text-gray-800">{a.client_first_name} {a.client_last_name}</div><div className="text-xs text-gray-500">{a.client_id} · {[a.client_city,a.client_state].filter(Boolean).join(', ') || 'Location not entered'}</div><div className="text-xs text-gray-400 mt-1">{a.client_phone || 'No phone'}</div></td>
                  <td className="py-4"><div>{a.assignment_type || 'Primary'} · {a.priority || 'Standard'}</div><div className="text-xs text-gray-500">{a.service_type || a.client_care_type || a.client_care_level || 'Service not specified'}</div><div className="text-xs text-gray-400">Started {a.start_date || '—'}</div></td>
                  <td className="py-4"><div>{a.weekly_hours ? `${a.weekly_hours} hrs/week` : 'Hours not set'}</div><div className="text-xs text-gray-500">{a.schedule_pattern || 'Pattern not set'}</div></td>
                  <td className="py-4"><select value={a.compatibility_status || 'Pending Review'} onChange={(e) => update(a.id, { compatibility_status: e.target.value })} className={`text-xs rounded-full px-2 py-1 border ${a.compatibility_status === 'Good Match' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{['Pending Review','Good Match','Trial Match','Needs Review'].map((v) => <option key={v}>{v}</option>)}</select></td>
                  <td className="py-4"><span className={`px-2 py-1 rounded-full text-xs ${a.status === 'Active' ? 'bg-green-100 text-green-700' : a.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>{a.end_date && <div className="text-xs text-gray-400 mt-1">Ended {a.end_date}</div>}</td>
                  <td className="py-4 px-4 text-right whitespace-nowrap"><a href={`/admin/clients/${a.client_id}`} className="text-blue-700 text-xs font-medium mr-3">Open Client</a>{a.status === 'Active' && <button onClick={() => setEnding(a)} className="text-red-600 text-xs font-medium">End</button>}</td>
                </tr>
              ))}
              {!visible.length && <tr><td colSpan={6} className="py-12 text-center text-gray-400">No assignments match this view.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {ending && <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"><div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5"><h3 className="font-semibold text-lg">End Client Assignment</h3><p className="text-sm text-gray-500 mt-1">{ending.client_first_name} {ending.client_last_name}</p><textarea value={endReason} onChange={(e) => setEndReason(e.target.value)} rows={4} placeholder="Reason for ending the assignment" className="w-full border rounded-lg px-3 py-2 mt-4 text-sm"/><div className="flex justify-end gap-2 mt-4"><button onClick={() => { setEnding(null); setEndReason(''); }} className="px-4 py-2 border rounded-lg text-sm">Cancel</button><button onClick={finishEnd} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm">End Assignment</button></div></div></div>}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>{children}</label>;
}

const SENTIMENT_OPTIONS = ["Positive", "Neutral", "Negative"];

function YesNoUndecided({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select className="w-full border rounded px-2 py-1.5 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );
}

function ExitInterviewTab({ employeeId, interviews, onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [interviewDate, setInterviewDate] = useState(new Date().toISOString().slice(0, 10));
  const [interviewerName, setInterviewerName] = useState("");
  const [whatWorkedWell, setWhatWorkedWell] = useState("");
  const [whatCouldImprove, setWhatCouldImprove] = useState("");
  const [wouldRecommend, setWouldRecommend] = useState("");
  const [interviewerRecommendsRehire, setInterviewerRecommendsRehire] = useState("");
  const [sentiment, setSentiment] = useState("");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const resetForm = () => {
    setInterviewDate(new Date().toISOString().slice(0, 10));
    setInterviewerName(""); setWhatWorkedWell(""); setWhatCouldImprove("");
    setWouldRecommend(""); setInterviewerRecommendsRehire(""); setSentiment(""); setComments("");
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await api.employees.exitInterviews.add(employeeId, {
        interview_date: interviewDate,
        interviewer_name: interviewerName || null,
        what_worked_well: whatWorkedWell || null,
        what_could_improve: whatCouldImprove || null,
        would_recommend_employer: wouldRecommend === "" ? null : wouldRecommend === "yes",
        interviewer_recommends_rehire: interviewerRecommendsRehire === "" ? null : interviewerRecommendsRehire === "yes",
        overall_sentiment: sentiment || null,
        comments: comments || null,
      });
      setShowForm(false);
      resetForm();
      onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => { await api.employees.exitInterviews.remove(id); onChange(); };

  const sentimentColor = {
    Positive: "bg-green-50 text-green-700",
    Neutral: "bg-gray-100 text-gray-600",
    Negative: "bg-red-50 text-red-700",
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowForm((s) => !s)}
          className="px-3 py-1.5 text-sm rounded text-white"
          style={{ backgroundColor: "#154E93" }}
        >
          {showForm ? "Cancel" : "+ Add Exit Interview"}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm p-5">
          {error && <div className="mb-3 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Interview Date</label>
              <input type="date" className="w-full border rounded px-2 py-1.5 text-sm" value={interviewDate} onChange={(e) => setInterviewDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Interviewer</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm" value={interviewerName} onChange={(e) => setInterviewerName(e.target.value)} />
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">What worked well?</label>
            <textarea rows={2} className="w-full border rounded px-2 py-1.5 text-sm" value={whatWorkedWell} onChange={(e) => setWhatWorkedWell(e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">What could improve?</label>
            <textarea rows={2} className="w-full border rounded px-2 py-1.5 text-sm" value={whatCouldImprove} onChange={(e) => setWhatCouldImprove(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <YesNoUndecided label="Would recommend employer to others" value={wouldRecommend} onChange={setWouldRecommend} />
            <YesNoUndecided label="Interviewer recommends rehire" value={interviewerRecommendsRehire} onChange={setInterviewerRecommendsRehire} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Overall Sentiment</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={sentiment} onChange={(e) => setSentiment(e.target.value)}>
                <option value="">—</option>
                {SENTIMENT_OPTIONS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Additional Comments</label>
            <textarea rows={2} className="w-full border rounded px-2 py-1.5 text-sm" value={comments} onChange={(e) => setComments(e.target.value)} />
          </div>
          <button onClick={submit} disabled={saving} className="px-3 py-1.5 text-sm rounded text-white disabled:opacity-60" style={{ backgroundColor: "#154E93" }}>
            {saving ? "Saving…" : "Save Exit Interview"}
          </button>
        </div>
      )}

      {interviews.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-6 text-center text-sm text-gray-400">No exit interviews recorded yet.</div>
      ) : (
        interviews.map((i) => (
          <div key={i.id} className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">{i.interview_date || "No date"} {i.interviewer_name ? `· ${i.interviewer_name}` : ""}</div>
              <div className="flex items-center gap-2">
                {i.overall_sentiment && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sentimentColor[i.overall_sentiment] || ""}`}>
                    {i.overall_sentiment}
                  </span>
                )}
                <button onClick={() => remove(i.id)} className="text-red-600 hover:underline text-xs">Remove</button>
              </div>
            </div>
            {i.what_worked_well && <p className="text-sm text-gray-700 mb-1"><span className="text-gray-400">Worked well: </span>{i.what_worked_well}</p>}
            {i.what_could_improve && <p className="text-sm text-gray-700 mb-1"><span className="text-gray-400">Could improve: </span>{i.what_could_improve}</p>}
            <div className="flex gap-4 text-xs text-gray-500 mt-2">
              {i.would_recommend_employer !== null && <span>Would recommend employer: {i.would_recommend_employer ? "Yes" : "No"}</span>}
              {i.interviewer_recommends_rehire !== null && <span>Interviewer recommends rehire: {i.interviewer_recommends_rehire ? "Yes" : "No"}</span>}
            </div>
            {i.comments && <p className="text-sm text-gray-600 mt-2 italic">{i.comments}</p>}
          </div>
        ))
      )}
    </div>
  );
}

const ONBOARDING_STATUS_COLORS = {
  "Not Started": "bg-gray-100 text-gray-500",
  "In Progress": "bg-yellow-50 text-yellow-700",
  "Completed": "bg-green-50 text-green-700",
};

function OnboardingTab({ employeeId, tasks, documents = [], onChange, onOpenDocuments = () => {} }) {
  const [seeding, setSeeding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [openCategories, setOpenCategories] = useState({});
  const [inspectionMode, setInspectionMode] = useState(false);

  const startChecklist = async () => {
    setSeeding(true);
    setError("");
    try {
      await api.employees.onboarding.seed(employeeId);
      onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setSeeding(false);
    }
  };

  const updateTask = async (id, patch) => {
    try {
      setError("");
      await api.employees.onboarding.update(id, patch);
      onChange();
    } catch (e) {
      setError(e.message);
    }
  };

  const syncChecklist = async () => {
    setSyncing(true);
    setError("");
    try {
      await api.employees.onboarding.reconcile(employeeId);
      await onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  };

  if (tasks.length === 0) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
        <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-2xl">✓</div>
        <h3 className="text-lg font-semibold text-gray-900">Start the employee onboarding workflow</h3>
        <p className="text-sm text-gray-500 mt-2 mb-5 max-w-xl mx-auto">
          CareOS will assign the correct hiring, screening, training, system-access, and role-readiness steps based on this employee's position.
        </p>
        {error && <div className="mb-3 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}
        <button onClick={startChecklist} disabled={seeding} className="px-5 py-2.5 text-sm rounded-lg text-white font-medium disabled:opacity-60" style={{ backgroundColor: "#154E93" }}>
          {seeding ? "Creating workflow…" : "Start Role-Based Onboarding"}
        </button>
      </div>
    );
  }

  const required = tasks.filter((t) => t.required !== 0);
  const completedCount = required.filter((t) => t.status === "Completed").length;
  const percent = required.length ? Math.round((completedCount / required.length) * 100) : 0;
  const blockingOpen = required.filter((t) => Number(t.blocks_activation) === 1 && t.status !== "Completed");
  const activationReady = required.length > 0 && blockingOpen.length === 0;
  const today = new Date().toISOString().slice(0, 10);
  const overdue = required.filter((t) => t.status !== "Completed" && t.due_date && t.due_date < today);
  const grouped = tasks.reduce((acc, task) => {
    const category = task.category || "General";
    if (!acc[category]) acc[category] = [];
    acc[category].push(task);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {error && <div className="text-sm bg-red-50 text-red-700 px-3 py-2 rounded-lg border border-red-100">{error}</div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-blue-900">Jybek 2026 role-based onboarding standard</p>
          <p className="text-xs text-blue-700 mt-0.5">Synchronize when an employee role changes or an older checklist contains facility-only tasks.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setInspectionMode((v) => !v)} className="shrink-0 px-4 py-2 rounded-lg text-xs font-semibold border border-blue-200 bg-white text-blue-800">
            {inspectionMode ? "Exit Inspection View" : "Inspection View"}
          </button>
          <button type="button" onClick={syncChecklist} disabled={syncing} className="shrink-0 px-4 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-60" style={{ backgroundColor: "#154E93" }}>
            {syncing ? "Synchronizing…" : "Sync 2026 Checklist"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border rounded-xl p-4 shadow-sm md:col-span-2">
          <div className="flex justify-between items-start gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Onboarding progress</p>
              <p className="text-3xl font-bold mt-1" style={{ color: "#154E93" }}>{percent}%</p>
              <p className="text-xs text-gray-500 mt-1">{completedCount} of {required.length} required steps completed</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${activationReady ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {activationReady ? "Ready for activation" : `${blockingOpen.length || required.length} activation blockers`}
            </span>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, backgroundColor: percent === 100 ? "#16a34a" : "#2563eb" }} />
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500">Overdue</p>
          <p className={`text-3xl font-bold mt-1 ${overdue.length ? "text-red-600" : "text-green-600"}`}>{overdue.length}</p>
          <p className="text-xs text-gray-500 mt-1">Incomplete past due date</p>
        </div>
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-gray-500">Remaining</p>
          <p className="text-3xl font-bold mt-1 text-amber-600">{required.length - completedCount}</p>
          <p className="text-xs text-gray-500 mt-1">Required steps left</p>
        </div>
      </div>

      {!activationReady && !inspectionMode && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-red-800">Cannot activate or schedule yet</p>
              <p className="text-xs text-red-600 mt-0.5">Complete the following activation blockers.</p>
            </div>
            <span className="text-xs font-semibold text-red-700">{blockingOpen.length} remaining</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {blockingOpen.slice(0, 10).map((task) => (
              <button key={task.id} type="button" onClick={() => { setOpenCategories((v) => ({ ...v, [task.category || "General"]: true })); setTimeout(() => document.getElementById(`onboarding-task-${task.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50); }} className="px-2.5 py-1 rounded-full bg-white border border-red-200 text-[11px] font-medium text-red-700 hover:bg-red-100">
                {task.task_type}
              </button>
            ))}
          </div>
        </div>
      )}

      {!inspectionMode && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
          {Object.entries(grouped).map(([category, categoryTasks]) => {
            const requiredInCategory = categoryTasks.filter((t) => t.required !== 0);
            const done = requiredInCategory.filter((t) => t.status === "Completed").length;
            const categoryPercent = requiredInCategory.length ? Math.round((done / requiredInCategory.length) * 100) : 0;
            return (
              <div key={category} className="bg-white border rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-800 truncate">{category}</span>
                  <span className="text-[11px] text-gray-500">{done}/{requiredInCategory.length}</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${categoryPercent}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-3">
        {Object.entries(grouped).map(([category, categoryTasks], index) => {
          const categoryDone = categoryTasks.filter((t) => t.status === "Completed").length;
          const isOpen = openCategories[category] ?? index < 2;
          return (
            <div key={category} className="bg-white border rounded-xl shadow-sm overflow-hidden">
              <button type="button" onClick={() => setOpenCategories((v) => ({ ...v, [category]: !isOpen }))} className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50">
                <div>
                  <span className="font-semibold text-gray-900">{category}</span>
                  <span className="ml-2 text-xs text-gray-500">{categoryDone}/{categoryTasks.length} complete</span>
                </div>
                <span className="text-gray-400">{isOpen ? "−" : "+"}</span>
              </button>
              {(inspectionMode || isOpen) && <div className="border-t divide-y">{categoryTasks.map((task) => <OnboardingTaskRow key={task.id} task={task} documents={documents} onOpenDocuments={onOpenDocuments} onUpdate={updateTask} inspectionMode={inspectionMode} />)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ONBOARDING_RECURRING_MONTHS = {
  "HIPAA Training": 12,
  "Infection Control / Standard Precautions": 12,
  "Dementia Training": 12,
  "Emergency Procedures": 12,
  "Initial BAAS Registry Check (NH)": 12,
  "Initial Non-Conviction Attestation": 12,
  "Driver License / Auto Insurance Review": 12,
  "LNA License Verification": 12,
};

const ONBOARDING_RULE_CONTEXT = {
  "HIPAA Training": ["Annual", "Client assignment", "Jybek privacy and HIPAA policy"],
  "Infection Control / Standard Precautions": ["Annual", "Client assignment", "Jybek infection-control policy"],
  "Dementia Training": ["Annual", "Applicable client assignment", "NH dementia-care requirements"],
  "Emergency Procedures": ["Annual", "Client assignment", "Jybek emergency-preparedness policy"],
  "Initial BAAS Registry Check (NH)": ["Annual", "Employment eligibility", "NH annual registry-check requirement"],
  "Initial Non-Conviction Attestation": ["Annual", "Employment eligibility", "NH personnel-file attestation requirement"],
  "Criminal Background Check": ["Initial / as required", "Employment eligibility", "NH personnel screening requirement"],
  "Orientation Completion": ["Initial", "Independent client assignment", "Jybek orientation standard"],
  "PCA Competency Review": ["Initial and as needed", "Independent client assignment", "Jybek PCA competency standard"],
  "LNA License Verification": ["At renewal", "LNA assignment", "Current NH LNA credential"],
};

function onboardingDateTone(dateValue) {
  if (!dateValue) return "border-gray-200 bg-white";
  const today = new Date();
  const due = new Date(`${dateValue}T23:59:59`);
  const days = Math.ceil((due - today) / 86400000);
  if (days < 0) return "border-red-300 bg-red-50 text-red-700";
  if (days <= 30) return "border-amber-300 bg-amber-50 text-amber-800";
  return "border-green-200 bg-green-50 text-green-800";
}

function addMonthsToISO(dateValue, months) {
  if (!dateValue || !months) return "";
  const date = new Date(`${dateValue}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.toISOString().slice(0, 10);
}

function renewalStatus(dateValue) {
  if (!dateValue) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${dateValue}T00:00:00`);
  const days = Math.ceil((due - today) / 86400000);
  if (days < 0) return { label: "Expired", detail: `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`, cls: "bg-red-50 text-red-700 border-red-200" };
  if (days <= 30) return { label: "Renewal urgent", detail: `${days} day${days === 1 ? "" : "s"} remaining`, cls: "bg-red-50 text-red-700 border-red-200" };
  if (days <= 60) return { label: "Renewal approaching", detail: `${days} days remaining`, cls: "bg-amber-50 text-amber-800 border-amber-200" };
  return { label: "Valid", detail: `${days} days remaining`, cls: "bg-green-50 text-green-700 border-green-200" };
}

const ONBOARDING_DOCUMENT_MATCHES = {
  "HIPAA Training": ["HIPAA Training", "HIPAA Certificate"],
  "Infection Control / Standard Precautions": ["Infection Control", "Standard Precautions"],
  "Dementia Training": ["Dementia Training", "Dementia Certificate"],
  "Emergency Procedures": ["Emergency Procedures", "Emergency Preparedness"],
  "Initial BAAS Registry Check (NH)": ["BAAS Registry Check", "Annual BAAS Registry Check"],
  "Initial Non-Conviction Attestation": ["Annual Non-Conviction Attestation", "Non-Conviction Attestation"],
  "Criminal Background Check": ["Criminal Background Check", "Background Check"],
  "Driver License / Auto Insurance Review": ["Driver License", "Auto Insurance"],
  "LNA License Verification": ["LNA License", "License Verification"],
};

function OnboardingTaskRow({ task, documents = [], onOpenDocuments, onUpdate, inspectionMode = false }) {
  const [notes, setNotes] = useState(task.notes || "");
  const today = new Date().toISOString().slice(0, 10);
  const overdue = task.status !== "Completed" && task.due_date && task.due_date < today;
  const blocked = Number(task.blocks_activation) === 1 && task.status !== "Completed";
  const recurringMonths = ONBOARDING_RECURRING_MONTHS[task.task_type] || 0;
  const context = ONBOARDING_RULE_CONTEXT[task.task_type] || [recurringMonths ? "Annual" : "One-time", blocked ? "Activation" : "Personnel file", "Jybek onboarding standard"];
  const renewal = renewalStatus(task.renewal_due_date);
  const acceptedTypes = ONBOARDING_DOCUMENT_MATCHES[task.task_type] || [task.task_type];
  const evidence = documents.find((doc) => acceptedTypes.some((name) => String(doc.document_type || "").toLowerCase().includes(name.toLowerCase())));

  const updateCompletedDate = (value) => {
    const patch = { completed_date: value };
    patch.renewal_due_date = value && recurringMonths ? addMonthsToISO(value, recurringMonths) : "";
    onUpdate(task.id, patch);
  };

  if (inspectionMode) {
    return (
      <div id={`onboarding-task-${task.id}`} className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-xs">
        <div className="col-span-4">
          <div className="font-medium text-gray-900">{task.task_type}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">{context[2]}</div>
        </div>
        <div className="col-span-2"><span className={`inline-flex px-2 py-1 rounded-full font-semibold ${ONBOARDING_STATUS_COLORS[task.status] || "bg-gray-100 text-gray-600"}`}>{task.status || "Not Started"}</span></div>
        <div className="col-span-2 text-gray-700">{task.completed_date || "—"}</div>
        <div className="col-span-2 text-gray-700">{recurringMonths ? (task.renewal_due_date || "—") : "One-time"}</div>
        <div className="col-span-2">{evidence?.id ? <a href={api.caregivers.documents.fileUrl(evidence.id)} target="_blank" rel="noreferrer" className="text-blue-700 font-medium">Open evidence</a> : <span className="text-red-600">Missing evidence</span>}</div>
      </div>
    );
  }

  return (
    <div id={`onboarding-task-${task.id}`} className="p-4">
      <div className="flex flex-col xl:flex-row xl:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{task.task_type}</span>
            {blocked && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700">Activation blocker</span>}
            {overdue && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Overdue</span>}
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-gray-500">
            <span><strong className="text-gray-600">Owner:</strong> {task.owner || "HR"}</span>
            <span><strong className="text-gray-600">Frequency:</strong> {context[0]}</span>
            <span><strong className="text-gray-600">Required before:</strong> {context[1]}</span>
            <span><strong className="text-gray-600">Standard:</strong> {context[2]}</span>
          </div>
          {task.due_date && task.status !== "Completed" && <div className="text-[11px] text-gray-500 mt-1"><strong>Initial completion due:</strong> {task.due_date}</div>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 xl:w-[650px]">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1">Status</span>
            <select value={task.status || "Not Started"} onChange={(e) => {
              const newStatus = e.target.value;
              const patch = { status: newStatus };
              if (newStatus === "Completed" && !task.completed_date) {
                const completed = new Date().toISOString().slice(0, 10);
                patch.completed_date = completed;
                if (recurringMonths) patch.renewal_due_date = addMonthsToISO(completed, recurringMonths);
              }
              onUpdate(task.id, patch);
            }} className={`w-full border rounded-lg px-2 py-2 text-xs font-medium ${ONBOARDING_STATUS_COLORS[task.status] || ""}`}>
              <option>Not Started</option><option>In Progress</option><option>Completed</option>
            </select>
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1">Completed On</span>
            <input type="date" className="w-full border rounded-lg px-2 py-2 text-xs" value={task.completed_date || ""} onChange={(e) => updateCompletedDate(e.target.value)} />
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1">Renewal Due</span>
            {recurringMonths ? (
              <input type="date" className={`w-full border rounded-lg px-2 py-2 text-xs ${onboardingDateTone(task.renewal_due_date)}`} value={task.renewal_due_date || ""} onChange={(e) => onUpdate(task.id, { renewal_due_date: e.target.value })} />
            ) : (
              <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-500">One-time requirement</div>
            )}
          </label>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {recurringMonths && renewal && <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${renewal.cls}`}>{renewal.label} · {renewal.detail}</span>}
        {evidence?.id ? <a href={api.caregivers.documents.fileUrl(evidence.id)} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">Open supporting evidence</a> : <button type="button" onClick={onOpenDocuments} className="inline-flex items-center rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-50">Attach supporting evidence</button>}
      </div>
      <input className="mt-3 w-full border rounded-lg px-3 py-2 text-xs" placeholder="Notes, verification source, or follow-up details" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => { if (notes !== (task.notes || "")) onUpdate(task.id, { notes }); }} />
    </div>
  );
}

function PhotoWidget({ employeeId, data, onChange }) {
  const [capturing, setCapturing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");
  const hasPhoto = Boolean(data.photo);
  const photoUrl = hasPhoto ? `${api.employees.photoUrl(employeeId)}?t=${data.updated_at || ""}` : null;

  const removePhoto = async () => {
    if (!window.confirm("Remove this employee's photo?")) return;
    setRemoving(true);
    setError("");
    try {
      await api.employees.removePhoto(employeeId);
      onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      {error && <div className="mb-3 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      {capturing || !hasPhoto ? (
        <PhotoCapture
          employeeId={employeeId}
          onUploaded={() => { setCapturing(false); onChange(); }}
          onCancel={hasPhoto ? () => setCapturing(false) : null}
        />
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <img
              src={photoUrl}
              alt="Employee"
              className="rounded object-cover border"
              style={{ width: "160px", height: "213px" }}
            />
            <button
              onClick={() => setCapturing(true)}
              title="Replace photo"
              className="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full flex items-center justify-center text-white shadow"
              style={{ backgroundColor: "#154E93" }}
            >
              📷
            </button>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${data.photo_approved ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
            {data.photo_approved ? "Photo Approved" : "Pending Approval"}
          </span>
          <button
            onClick={() => setCapturing(true)}
            className="w-full px-3 py-2 text-sm rounded text-white font-medium"
            style={{ backgroundColor: "#154E93" }}
          >
            Upload New Photo
          </button>
          <button
            onClick={removePhoto}
            disabled={removing}
            className="w-full px-3 py-2 text-sm rounded border text-red-600 border-red-200 disabled:opacity-60"
          >
            {removing ? "Removing…" : "Remove Photo"}
          </button>
        </div>
      )}
    </div>
  );
}

// Automatic center-crop to a PORTRAIT rectangle (3:4), matching the
// passport-style headshot look — not square. No manual drag-to-reposition
// UI, keeping this dependency-free; works for both an <img> (file upload)
// and a <video> element (camera capture).
function cropToPortraitDataUrl(sourceEl) {
  const width = sourceEl.naturalWidth || sourceEl.videoWidth;
  const height = sourceEl.naturalHeight || sourceEl.videoHeight;
  const targetRatio = 3 / 4; // width:height
  let cropW = width;
  let cropH = width / targetRatio;
  if (cropH > height) {
    cropH = height;
    cropW = height * targetRatio;
  }
  const sx = (width - cropW) / 2;
  const sy = (height - cropH) / 2;
  const OUTPUT_W = 375;
  const OUTPUT_H = 500;
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_W;
  canvas.height = OUTPUT_H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(sourceEl, sx, sy, cropW, cropH, 0, 0, OUTPUT_W, OUTPUT_H);
  return canvas.toDataURL("image/jpeg", 0.88);
}

function PhotoCapture({ employeeId, onUploaded, onCancel }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    img.onload = () => setPreviewUrl(cropToPortraitDataUrl(img));
    img.src = URL.createObjectURL(file);
  };

  const startCamera = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 0);
    } catch (e) {
      setError("Could not access camera — check browser permissions.");
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    setPreviewUrl(cropToPortraitDataUrl(videoRef.current));
    stopCamera();
  };

  const upload = async () => {
    if (!previewUrl) return;
    setUploading(true);
    setError("");
    try {
      await api.employees.uploadPhoto(employeeId, previewUrl);
      setPreviewUrl(null);
      onUploaded();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {error && <div className="mb-3 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      {previewUrl ? (
        <div className="flex flex-col items-center gap-3">
          <img src={previewUrl} alt="Preview" className="rounded object-cover border" style={{ width: "160px", height: "213px" }} />
          <div className="flex gap-2">
            <button onClick={() => setPreviewUrl(null)} className="px-3 py-1.5 text-sm rounded border">Retake</button>
            <button
              onClick={upload}
              disabled={uploading}
              className="px-3 py-1.5 text-sm rounded text-white disabled:opacity-60"
              style={{ backgroundColor: "#154E93" }}
            >
              {uploading ? "Uploading…" : "Use This Photo"}
            </button>
          </div>
        </div>
      ) : cameraActive ? (
        <div className="flex flex-col items-center gap-3">
          <video ref={videoRef} autoPlay playsInline className="rounded object-cover border bg-black" style={{ width: "213px", height: "213px" }} />
          <div className="flex gap-2">
            <button onClick={stopCamera} className="px-3 py-1.5 text-sm rounded border">Cancel</button>
            <button onClick={capturePhoto} className="px-3 py-1.5 text-sm rounded text-white" style={{ backgroundColor: "#154E93" }}>
              Capture
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="w-full flex gap-2 flex-wrap justify-center">
            <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 text-sm rounded border">
              Upload Photo
            </button>
            <button onClick={startCamera} className="px-3 py-1.5 text-sm rounded border">Use Camera</button>
            {onCancel && <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border">Cancel</button>}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </div>
      )}
    </div>
  );
}

// Compact ID Card status + inline Front/Back preview, embedded directly
// on Summary. Reuses IDCardFront/IDCardBack from the shared component
// (same markup as the full print page) wrapped in a CSS scale transform
// — one source of truth for the card design, this is just a smaller view
// of the exact same thing.
function IdCardPreviewCard({ employeeId, data, onChange }) {
  const [side, setSide] = useState("Front");
  const [approving, setApproving] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [error, setError] = useState("");
  const hasPhoto = Boolean(data.photo);

  useEffect(() => {
    if (hasPhoto) {
      api.employees.qrCode(employeeId).then((r) => setQrCode(r.qr_code)).catch(() => {});
    }
  }, [employeeId, hasPhoto, data.updated_at]);

  const approve = async () => {
    setApproving(true);
    setError("");
    try {
      await api.employees.approvePhoto(employeeId);
      onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setApproving(false);
    }
  };

  const displayName = `${data.preferred_name || data.first_name} ${data.last_name}`.trim();
  const photoUrl = hasPhoto ? `${api.employees.photoUrl(employeeId)}?t=${data.updated_at || ""}` : null;
  // Sized to fill the card widget without leaving a visible gap on the
  // side — 0.72 left noticeable dead space next to a small card, this
  // fills the available width much closer to edge-to-edge.
  const PREVIEW_SCALE = 0.88;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold" style={{ color: "#154E93" }}>ID Card</h3>
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${data.photo_approved ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
          {data.photo_approved ? "Active" : "Not Active"}
        </span>
      </div>
      {error && <div className="mb-3 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      <div className="grid grid-cols-4 gap-1.5 my-3">
        {[
          { label: "Photo", done: hasPhoto },
          { label: "Approve", done: Boolean(data.photo_approved) },
          { label: "Dates", done: Boolean(data.id_card_issue_date && data.id_card_expiration_date) },
          { label: "Issue", done: Boolean(data.photo_approved && data.id_card_issue_date) },
        ].map((step, index) => (
          <div key={step.label} className={`rounded-lg border px-1.5 py-2 text-center ${step.done ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-100"}`}>
            <div className={`mx-auto mb-1 w-5 h-5 rounded-full text-[10px] font-bold inline-flex items-center justify-center ${step.done ? "bg-green-600 text-white" : "bg-white border text-gray-400"}`}>{step.done ? "✓" : index + 1}</div>
            <div className={`text-[9px] font-semibold ${step.done ? "text-green-700" : "text-gray-400"}`}>{step.label}</div>
          </div>
        ))}
      </div>
      <InfoRow label="Card Issue Date" value={formatFriendlyDate(data.id_card_issue_date)} />
      <InfoRow label="Card Expiration Date" value={formatFriendlyDate(data.id_card_expiration_date)} />

      {!hasPhoto ? (
        <p className="text-sm text-gray-400 mt-3">Upload a photo to generate the ID card preview.</p>
      ) : (
        <>
          <div className="flex gap-1 mt-4 mb-3 justify-center">
            {["Front", "Back"].map((s) => (
              <button
                key={s}
                onClick={() => setSide(s)}
                className={`px-3 py-1 text-xs rounded border ${side === s ? "text-white" : "text-gray-600"}`}
                style={side === s ? { backgroundColor: "#154E93", borderColor: "#154E93" } : {}}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="flex justify-center">
            <div
              style={{
                width: `calc(3.375in * ${PREVIEW_SCALE})`,
                height: `calc(2.125in * ${PREVIEW_SCALE})`,
                overflow: "hidden",
              }}
            >
              <div style={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: "top left" }}>
                {side === "Front" ? (
                  <IDCardFront data={data} photoUrl={photoUrl} qrCode={qrCode} displayName={displayName} />
                ) : (
                  <IDCardBack data={data} displayName={displayName} />
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">ID Card Actions</h4>
            {!data.photo_approved && (
              <button
                onClick={approve}
                disabled={approving}
                className="w-full mb-2 px-3 py-2 text-sm rounded text-white disabled:opacity-60"
                style={{ backgroundColor: "#154E93" }}
              >
                {approving ? "Approving…" : "Approve Photo & Activate Card"}
              </button>
            )}
            {/* Always visible, not conditionally hidden — a locked button
                the admin can see (and understand why it's locked) is far
                clearer than a button that silently doesn't exist yet. */}
            {data.photo_approved && data.id_card_issue_date && data.id_card_expiration_date ? (
              <a
                href={`/admin/employees/${employeeId}/id-card-print`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-3 py-2 text-sm rounded text-white font-medium"
                style={{ backgroundColor: "#154E93" }}
              >
                Print ID Card
              </a>
            ) : (
              <div>
                <button
                  disabled
                  className="w-full px-3 py-2 text-sm rounded text-gray-400 bg-gray-100 cursor-not-allowed font-medium"
                >
                  Print ID Card
                </button>
                <p className="text-xs text-gray-400 mt-1.5 text-center">Approve the photo to activate the card. Issue and expiration dates will be created automatically when missing.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

